// ============================================================
// HTTP ApiClient — talks to the NestJS backend (session cookies).
// Replaces the Phase-1 mock adapter.
// ============================================================
import type { AggFilter, AggOp, AggQuery, AggResult, ChartSpec, LoginRequest, Role, SessionUser } from "@webgis/shared";
import type { ApiClient } from "./client";
import { demoAbfangen, demoAbfangenForm, demoModusSetzen, demoUeberlagern } from "../lib/demo-simulation";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** maschinenlesbarer Fehlercode aus dem Antwortkoerper (z. B. 'AUDIT_UNAVAILABLE') —
     *  der Text daneben ist fuer Menschen und darf sich jederzeit aendern. */
    readonly code?: string,
  ) {
    super(message);
  }
}

/**
 * `cache: "no-store"` is not a micro-optimisation, it is load-bearing.
 *
 * If Traefik ever answers an /api request from the frontend container (it does,
 * for the seconds a backend redeploy is in flight), the reply is the SPA shell:
 * `200 text/html`. A browser is entitled to cache that under the *API* URL, and
 * without an explicit Cache-Control it applies heuristic freshness — so every
 * later call was served HTML straight from the disk cache, no request on the
 * wire, and the app died on `Unexpected token '<'` long after the backend was
 * healthy again. Nothing server-side can undo an entry that is already in a
 * user's cache. "no-store" bypasses the HTTP cache in both directions, so the
 * API can never be poisoned this way.
 */
const NO_STORE: Pick<RequestInit, "cache" | "credentials"> = { cache: "no-store", credentials: "same-origin" };

/**
 * `Cache-Control: no-store` — von Hand gesetzt, und genau darin liegt der Witz.
 *
 * Die Fetch-Spezifikation haengt bei cache-mode "no-store"/"reload" von SELBST
 * `Cache-Control: no-cache` und `Pragma: no-cache` an — aber nur, wenn die
 * Kopfliste noch KEIN `cache-control` traegt. Express' fresh() prueft genau
 * darauf (`/(?:^|,)\s*?no-cache\s*?(?:,|$)/`) und liefert dann immer false:
 * res.json() antwortet mit 200 und vollem Koerper, nie mit 304. Der
 * ETag-Kurzschluss weiter unten feuerte damit im Browser NIE — nur in curl,
 * das diesen Kopf nicht anhaengt.
 *
 * Am Live-System nachgemessen (GET /api/projects, voller Koerper 3442 B):
 *   If-None-Match allein                     -> 304,    0 B
 *   If-None-Match + Cache-Control: no-cache  -> 200, 3442 B
 *   If-None-Match + Cache-Control: no-store  -> 304,    0 B
 * "no-store" haelt den Browsercache genauso aussen vor wie das angehaengte
 * "no-cache" (die SPA-Huelle von oben kann also weiterhin nirgends liegen
 * bleiben), trifft die Regex von fresh() aber nicht. Beide Ziele auf einmal.
 *
 * Cache-Control steht NICHT auf der CORS-Freiliste: der Kopf ist nur deshalb
 * kostenlos, weil alle Wege hier relativ auf /api zeigen und damit
 * gleichherkuenftig sind. Wer je eine absolute Fremdadresse einsetzt, handelt
 * sich damit einen Preflight je Anfrage ein.
 */
const CACHE_KOPF = { "Cache-Control": "no-store" } as const;

/** Turn a non-JSON reply into a legible error instead of a JSON.parse crash.
 *
 * `roh` ist das Nebenfach fuer den ETag-Speicher: er braucht den Rohtext, um
 * die Groesse zu MESSEN statt sie aus einem Kopf zu raten (siehe
 * ETAG_MAX_ZEICHEN). Der Waechter darueber bleibt damit die einzige Tuer in den
 * Speicher — was hier mit ApiError herausfliegt, wird nie gemerkt. */
async function parseJson<T>(res: Response, roh?: { text: string }): Promise<T> {
  const ct = res.headers.get("content-type") ?? "";
  if (!ct.includes("json")) {
    await res.text().catch(() => undefined); // drain so the connection can be reused
    throw new ApiError(res.status, "Server nicht erreichbar — bitte neu laden.");
  }
  // Rohtext statt res.json(): fuer jeden anderen Aufrufer aendert das nichts
  // (JSON.parse wirft denselben SyntaxError), fuer den Speicher ist es der
  // Unterschied zwischen messen und raten.
  const text = await res.text();
  const daten = JSON.parse(text) as T;
  if (roh) roh.text = text;
  return daten;
}

/** Best-effort message + Code from an error reply; falls back to the status text. */
async function errorInfo(res: Response): Promise<{ message: string; code?: string }> {
  try {
    const j = (await res.json()) as { message?: string | string[]; code?: unknown };
    const m = Array.isArray(j.message) ? j.message.join(", ") : j.message;
    return { message: m ?? res.statusText, code: typeof j.code === "string" ? j.code : undefined };
  } catch {
    return { message: res.statusText };
  }
}

/** aus einer Fehlerantwort den fertigen ApiError bauen (Status + Text + Code) */
async function apiError(res: Response): Promise<ApiError> {
  const { message, code } = await errorInfo(res);
  return new ApiError(res.status, message, code);
}

/**
 * Wird gerufen, wenn der Server eine gueltige Sitzung erwartet und keine
 * vorfindet. Als Rueckruf und nicht als direkter Zugriff auf den Zustand:
 * useStore importiert seinerseits Typen von hier, und ein Ringschluss
 * zwischen beiden Modulen waere zur Ladezeit ein `undefined`.
 */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

/**
 * Abgelaufene Sitzung erkennen — aber NUR dort, wo 401 wirklich „nicht
 * angemeldet" heisst.
 *
 * Die Anmeldewege sind ausgenommen: ein falsches Kennwort antwortet ebenfalls
 * mit 401, und daraufhin den Zustand zu leeren wuerde eine Anmeldeseite
 * zurueckwerfen, auf der man ohnehin schon steht. `/auth/me` ebenso — das ist
 * die Frage „bin ich angemeldet?", und ein Nein darauf ist kein Ereignis.
 */
function maybeSignedOut(status: number, path: string): void {
  if (status !== 401) return;
  if (path.startsWith("/auth/")) return;
  // Die gemerkten Antworten gehoerten dieser Sitzung — mit ihr fliegen sie raus.
  etagSpeicherLeeren();
  onUnauthorized?.();
}

/**
 * ETag-Kurzschluss — der Gegenpol zu `no-store` weiter oben.
 *
 * `no-store` schuetzt vor der vergifteten SPA-Huelle, nimmt dem Browser dabei
 * aber auch das Nachfragen: er darf nichts behalten, also auch nichts
 * revalidieren, und laedt bei JEDEM Neuladen alles voll herunter. Gemessen an
 * einem gewoehnlichen Projektaufruf (Projektliste, Projekt, Ebenen, Stats,
 * Filter, Kartenansichten, Diagramme, voller Spaltenkatalog): 8 Antworten,
 * zusammen 118 370 B — davon 99 138 B allein der Spaltenkatalog —, die der
 * Server auf If-None-Match samt und sonders mit 304 und null Byte Koerper
 * beantwortet. Statt „no-cache" — das waere die bequeme Loesung
 * und holte sich genau das Risiko zurueck, gegen das der Kommentar oben
 * geschrieben ist: die HTML-Antwort laege wieder im Browsercache — merken wir
 * uns ETag und ROHTEXT der Antwort SELBST, setzen If-None-Match von Hand und
 * parsen bei 304 den gemerkten Text erneut.
 *
 * Der Browsercache bleibt damit aussen vor, und der Waechter in parseJson
 * bleibt die einzige Tuer, durch die ein Koerper hier hineinkommt: eine
 * HTML-Huelle kann gar nicht erst gemerkt werden.
 *
 * ⚠ Bis 08/2026 sparte dieser ganze Block NULL Bytes: der Browser haengte
 * hinter unserem Ruecken `Cache-Control: no-cache` an, womit fresh() immer
 * false lieferte (siehe CACHE_KOPF oben). Die Zahl, die hier frueher stand,
 * stammte aus einer curl-Messung — curl haengt den Kopf nicht an und sah 304,
 * der Browser nie. Wer an den Koepfen dreht, misst das bitte mit einem
 * ANGEHAENGTEN `Cache-Control: no-cache` nach, nicht mit blankem curl.
 */
const ETAG_MAX = 40;
/**
 * Deckel je Eintrag — gemessen am ROHTEXT der Antwort, ausdruecklich NICHT am
 * Kopf `content-length`.
 *
 * Den Kopf gibt es hinter der Kompression naemlich gar nicht — und zwar
 * ausgerechnet dort nicht, wo die Bremse gebraucht wird. Nachgemessen am
 * Live-System mit `curl -sI -H "Accept-Encoding: gzip, br, zstd"`: eine kleine
 * Antwort (13 B) traegt content-length und kein content-encoding, eine grosse
 * traegt `content-encoding: gzip` und KEIN content-length — Traefik streamt
 * komprimiert und entfernt den Kopf, die compress-Middleware haengt an beiden
 * webgis-Routern. Genau die grossen Antworten kommen also ohne Mass an:
 * `Number(null ?? 0)` ist 0, `0 <= 2 000 000` ist immer wahr, die Bremse haette
 * in Produktion NIE gegriffen und der Speicher haette 40 vollstaendige
 * Antworten beliebiger Groesse gehalten. Der Rohtext liegt uns beim Parsen ohnehin vor und misst genau das,
 * was wir wirklich behalten. Gezaehlt werden UTF-16-Zeichen, nicht Bytes —
 * fuer eine Obergrenze ist das die ehrlichere Zahl als die Leitungsgroesse.
 */
const ETAG_MAX_ZEICHEN = 2_000_000;

/**
 * Deckel ueber die SUMME aller Eintraege — der Einzeldeckel allein reicht nicht.
 *
 * ETAG_MAX (40) mal ETAG_MAX_ZEICHEN (2 Mio) waeren im schlimmsten Fall rund
 * 160 MB Rohtext im Tab, und zwar ZUSAETZLICH zu den geparsten Baeumen, die
 * react-query fuer genau dieselben Daten haelt. Geleert wird hier nur beim
 * Sitzungswechsel (An-/Abmelden, 401) — eine lange Kartensitzung, die viele
 * grosse /columns-, /rows- und /query/list-Antworten beruehrt, haelt sonst
 * ueber Stunden zweistellige Megabyte fuer nichts.
 *
 * 4 Mio UTF-16-Zeichen sind rund 8 MB Heap. Gemessen kostet ein vollstaendiger
 * Projektaufruf 118 370 Zeichen (siehe Blockkommentar oben) — die Grenze traegt
 * also ein Vielfaches dessen, was eine Sitzung wirklich anfasst, und deckelt
 * den schlimmsten Fall trotzdem auf eine Groessenordnung, die neben dem
 * Query-Cache nicht auffaellt. Der Einzeldeckel liegt bewusst darunter: ein
 * einzelner Riese kann den Speicher damit nie ganz leerraeumen, es passen
 * immer noch zwei.
 */
const ETAG_SUMME_MAX = 4_000_000;

/** Laufende Summe der gemerkten Zeichen — mitgefuehrt statt bei jeder Verdraengung neu aufaddiert. */
let etagZeichen = 0;

/**
 * Gemerkt wird der ROHTEXT, nicht der geparste Koerper.
 *
 * Der Aufrufer legt das Ergebnis im Query-Cache ab; was dort damit geschieht,
 * darf den naechsten 304 nicht veraendern. Frueher stand hier structuredClone
 * ueber dem geparsten Koerper — ein tiefer Durchlauf ueber die GANZE Antwort
 * auf dem Hauptfaden bei JEDEM Treffer, und die Notbremse „gibt es
 * structuredClone nicht, dann eben ohne Kopie" reichte genau die geteilte
 * Referenz heraus, gegen die sie schuetzen sollte. JSON.parse je Treffer
 * kostet dieselbe Groessenordnung, liefert aber ohne Ausnahmefall immer einen
 * frischen Baum. Einfrieren waere billiger, verbietet dem Aufrufer aber jedes
 * Sortieren an Ort und Stelle — im strict mode eines Moduls kein stiller
 * No-op, sondern ein TypeError mitten in einer fremden Ansicht. Nebenbei ist
 * der Text der kleinere Rest im Speicher: der geparste Baum ist regelmaessig
 * ein Vielfaches davon.
 */
const etagSpeicher = new Map<string, { etag: string; text: string }>();

/** Leert den Speicher — bei jedem Sitzungswechsel (An-/Abmelden, 401). */
export function etagSpeicherLeeren(): void {
  etagSpeicher.clear();
  etagZeichen = 0;
}

/**
 * Einen Eintrag vergessen — IMMER hierueber und nie mit etagSpeicher.delete(),
 * sonst laeuft die mitgefuehrte Summe von der Wirklichkeit weg und die Bremse
 * greift irgendwann zu frueh oder gar nicht mehr.
 */
function etagVergessen(schluessel: string): void {
  const alt = etagSpeicher.get(schluessel);
  if (!alt) return;
  etagZeichen -= alt.text.length;
  etagSpeicher.delete(schluessel);
}

/** Merken mit Verdraengung: Map haelt die Einfuegereihenfolge, der aelteste Eintrag fliegt. */
function etagMerken(schluessel: string, etag: string, text: string): void {
  etagVergessen(schluessel); // neu einsetzen = ans Ende der Reihe
  etagSpeicher.set(schluessel, { etag, text });
  etagZeichen += text.length;
  // Verdraengt wird nach ANZAHL und nach SUMME. Der gerade eingesetzte Eintrag
  // ist dabei tabu: sonst wuerfe sich ein Eintrag, der fuer sich allein schon
  // ueber der Summengrenze liegt, selbst wieder weg — die Schleife liefe leer
  // und der Aufrufer bekaeme nie einen Treffer.
  while (etagSpeicher.size > ETAG_MAX || etagZeichen > ETAG_SUMME_MAX) {
    const aeltester = etagSpeicher.keys().next().value;
    if (aeltester === undefined || aeltester === schluessel) break;
    etagVergessen(aeltester);
  }
}

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  // Demo-Sitzung: die Simulation antwortet DIESSEITS von fetch. Das ist der
  // ganze Witz dieser Stelle — es entsteht kein Request, `parseJson` wird nie
  // gefragt (die Faelschung braucht also keinen content-type), und `apiError`
  // kann nicht zuschlagen. Ausserhalb der Demo kostet die ganze Schicht diesen
  // einen Aufruf, der sofort mit null zurueckkommt.
  const simuliert = demoAbfangen<T>(method, path, body);
  if (simuliert) {
    if (simuliert.art === "absage") throw new ApiError(403, simuliert.text, "DEMO_READONLY");
    return simuliert.wert;
  }
  // Nur lesende Aufrufe ohne Koerper koennen einen ETag tragen.
  const schluessel = method === "GET" && body === undefined ? path : null;
  const gemerkt = schluessel ? etagSpeicher.get(schluessel) : undefined;
  // CACHE_KOPF zuerst: ohne ihn haengt der Browser bei cache "no-store" von
  // selbst `Cache-Control: no-cache` an und If-None-Match erreicht fresh() nie.
  const headers: Record<string, string> = { ...CACHE_KOPF };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (gemerkt) headers["If-None-Match"] = gemerkt.etag;
  const res = await fetch(`/api${path}`, {
    ...NO_STORE,
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  // 304 ist kein Fehler und kommt nur, weil WIR gefragt haben: unveraendert.
  if (res.status === 304 && schluessel && gemerkt) {
    etagMerken(schluessel, gemerkt.etag, gemerkt.text); // gerade benutzt -> ans Ende
    // Frisch geparst = frischer Baum fuer diesen einen Aufrufer; der gemerkte
    // Text bleibt unberuehrt, egal was mit dem Ergebnis noch geschieht. Und
    // herausgereicht wird hier ausschliesslich, was frueher schon durch den
    // Waechter kam — selbst ein 304 aus falscher Hand koennte keine HTML-Huelle
    // hervorzaubern, sondern hoechstens den zuletzt geprueften JSON-Stand.
    return demoUeberlagern<T>(method, path, body, JSON.parse(gemerkt.text) as T);
  }
  if (!res.ok) {
    maybeSignedOut(res.status, path);
    throw await apiError(res);
  }
  // Den Rohtext nur einsammeln, wo er auch gebraucht wird (lesende Aufrufe).
  const roh = schluessel ? { text: "" } : undefined;
  const daten = await parseJson<T>(res, roh);
  if (schluessel) {
    const etag = res.headers.get("etag");
    // Gemerkt wird ausschliesslich, was den Waechter in parseJson passiert hat:
    // die SPA-Huelle (200 text/html waehrend eines Backend-Deploys) fliegt dort
    // mit ApiError heraus und erreicht diese Zeile nie. Der Speicher kann sie
    // also weder aufnehmen noch spaeter ausliefern.
    // Ohne ETag kein Kurzschluss — und einen alten Eintrag dann auch loeschen,
    // sonst fragen wir spaeter mit einem Kennzeichen, das der Server nicht mehr kennt.
    if (etag && roh && roh.text.length <= ETAG_MAX_ZEICHEN) etagMerken(schluessel, etag, roh.text);
    else etagVergessen(schluessel);
  }
  // Ueberlagert wird ERST hier, nach dem Merken: im ETag-Speicher liegt
  // ausschliesslich Serverwahrheit. Legte man den ueberlagerten Stand hinein,
  // faelschte sich die Demo in einen Speicher, den ein echter 304 ueberlebt —
  // und die Faelschung stuende dann auch da noch, wo sie nicht hingehoert.
  return demoUeberlagern<T>(method, path, body, daten);
}

// multipart POST — let the browser set the Content-Type (with boundary)
async function postForm<T>(path: string, form: FormData): Promise<T> {
  // Zweite Einhaengung der Demo-Simulation: `postForm` ruft `req` NICHT auf,
  // die Datei-Uploads kaemen sonst am Abfangen vorbei.
  const simuliert = demoAbfangenForm<T>(path, form);
  if (simuliert) {
    if (simuliert.art === "absage") throw new ApiError(403, simuliert.text, "DEMO_READONLY");
    return simuliert.wert;
  }
  // Kein Content-Type von Hand — den setzt der Browser samt boundary. CACHE_KOPF
  // aendert daran nichts und haelt die Regel an einer einzigen Stelle.
  const res = await fetch(`/api${path}`, { ...NO_STORE, method: "POST", headers: { ...CACHE_KOPF }, body: form });
  if (!res.ok) {
    maybeSignedOut(res.status, path);
    throw await apiError(res);
  }
  return parseJson<T>(res);
}

/**
 * POST mit Dateiantwort (Excel) — dieselben Riegel wie req(): Demo-Abfang,
 * no-store, Abmelde-Erkennung. Nur der JSON-Waechter entfaellt; an seine Stelle
 * tritt die Pruefung auf die SPA-Huelle, damit waehrend eines Deploys keine
 * HTML-Seite als .xlsx auf der Platte landet.
 */
async function postDatei(path: string, body: unknown): Promise<{ blob: Blob; dateiname: string | null }> {
  const simuliert = demoAbfangen<unknown>("POST", path, body);
  if (simuliert) throw new ApiError(403, simuliert.art === "absage" ? simuliert.text : "In der Demo nicht verfügbar.", "DEMO_READONLY");
  const res = await fetch(`/api${path}`, {
    ...NO_STORE,
    method: "POST",
    headers: { ...CACHE_KOPF, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    maybeSignedOut(res.status, path);
    throw await apiError(res);
  }
  if ((res.headers.get("content-type") ?? "").includes("text/html")) {
    await res.text().catch(() => undefined);
    throw new ApiError(502, "Der Server ist gerade nicht erreichbar — bitte gleich noch einmal versuchen.");
  }
  const dateiname = /filename="([^"]+)"/.exec(res.headers.get("content-disposition") ?? "")?.[1] ?? null;
  return { blob: await res.blob(), dateiname };
}

/**
 * GET mit Dateiantwort (Kalenderdatei). Dieselben Riegel wie postDatei; in der
 * Demo greift kein Abfang, weil ein Herunterladen nichts ändert.
 */
async function getDatei(path: string): Promise<{ blob: Blob; dateiname: string | null }> {
  const res = await fetch(`/api${path}`, { ...NO_STORE, headers: { ...CACHE_KOPF } });
  if (!res.ok) {
    maybeSignedOut(res.status, path);
    throw await apiError(res);
  }
  if ((res.headers.get("content-type") ?? "").includes("text/html")) {
    await res.text().catch(() => undefined);
    throw new ApiError(502, "Der Server ist gerade nicht erreichbar — bitte gleich noch einmal versuchen.");
  }
  const dateiname = /filename="([^"]+)"/.exec(res.headers.get("content-disposition") ?? "")?.[1] ?? null;
  return { blob: await res.blob(), dateiname };
}

const get = <T>(p: string) => req<T>("GET", p);
const post = <T>(p: string, b?: unknown) => req<T>("POST", p, b);
const patch = <T>(p: string, b?: unknown) => req<T>("PATCH", p, b);
const put = <T>(p: string, b?: unknown) => req<T>("PUT", p, b);
const del = <T>(p: string) => req<T>("DELETE", p);

/**
 * Spaltenkatalog holen. Eigene Funktion und kein Pfeil im Objektliteral, weil
 * nur eine deklarierte Funktion Ueberladungen tragen kann: die schlanke und die
 * volle Antwort haben verschiedene Typen, und ein gemeinsamer Vereinigungstyp
 * wuerde die Unterscheidung genau dort verwischen, wo sie gebraucht wird.
 */
function ladeSpalten(id: string, opts: { slim: true }): Promise<ApiColumnSlim[]>;
function ladeSpalten(id: string, opts?: { slim?: false }): Promise<ApiColumn[]>;
function ladeSpalten(id: string, opts: { slim?: boolean }): Promise<ApiColumnSlim[]>;
function ladeSpalten(id: string, opts?: { slim?: boolean }): Promise<ApiColumn[] | ApiColumnSlim[]> {
  // Der Parameter gehoert in den PFAD, nicht in einen Kopf: damit sind schlanke
  // und volle Antwort auch fuer den ETag-Speicher zwei getrennte Eintraege
  // (dort ist der Pfad der Schluessel) und koennen einander nicht ueberschreiben.
  return get<ApiColumn[] | ApiColumnSlim[]>(`/layers/${id}/columns${opts?.slim ? "?slim=1" : ""}`);
}

/**
 * Query-Key des Spaltenkatalogs — EIN Ort, damit voll und schlank nie im
 * selben Cache-Eintrag landen.
 *
 * Der Vertrag, den die Aufrufstellen einhalten muessen:
 *   voll    api.layers.columns(id)                  -> ["columns", id]
 *   schlank api.layers.columns(id, { slim: true })  -> ["columns", id, "slim"]
 * IMMER beides gemeinsam setzen — Aufruf und Key gehoeren zusammen. Wer die
 * schlanke Antwort unter dem vollen Key ablegt, verliert `config`, `input`,
 * `decimals`, `editable` und `nullable` fuer JEDEN spaeteren Leser desselben
 * Keys: der Formulareditor bekaeme dann die schlanke Fassung aus dem Cache und
 * schriebe beim naechsten Speichern Wertebeziehungen, Eingabeart und
 * Nachkommastellen weg. Ein stiller Datenverlust, den niemand beim Klicken
 * sieht — deshalb dieser Helfer statt handgeschriebener Arrays.
 *
 * Der schlanke Key ist bewusst eine VERLAENGERUNG des vollen: react-query
 * gleicht Praefixe ab, `invalidateQueries({ queryKey: ["columns", id] })`
 * trifft also weiterhin beide Fassungen. Das ist richtig so — beide stammen
 * aus derselben Serverwahrheit und veralten gemeinsam (siehe realtime.ts).
 *
 * Der ETag-Speicher in req() trennt die beiden ohnehin von selbst: `?slim=1`
 * steht im PFAD, und der Pfad ist dort der Schluessel.
 */
export function spaltenKey(id: string, opts?: { slim?: boolean }): ["columns", string] | ["columns", string, "slim"] {
  return opts?.slim ? ["columns", id, "slim"] : ["columns", id];
}

// ---- API shapes (mirror backend controllers) ----

export interface ApiProject {
  id: string;
  name: string;
  schema: string;
  status: "active" | "paused" | "archived" | "pending";
  /**
   * Alter Weg: das eingebettete Bild steckte als base64-JPEG in JEDER
   * Projektantwort (gemessen 982 029 B, 99,7 % der Liste). Neue Server
   * liefern hier nur noch einen etwaigen FREMDVERWEIS (http/Pfad), bei einem
   * eingebetteten Bild null — gelesen wird es dann als eigene Datei ueber
   * /api/projects/:id/thumb?w=… (siehe `thumb`). Geschrieben wird weiterhin
   * hierueber: der Editor schickt die data:-URL per PATCH.
   */
  thumbUrl: string | null;
  /** true = das Projekt hat ein Bild. Fehlt bei aelteren Servern; siehe thumbQuelle(). */
  thumb?: boolean;
  /**
   * Inhaltsstempel des Bildes (8 Zeichen md5). Haengt als `&v=` an der
   * Bildadresse — und NUR damit liefert /thumb `immutable` aus. Ohne Stempel
   * bleibt die Adresse beim Bildtausch dieselbe, und der Browser zeigte ein
   * Jahr lang das alte Bild.
   */
  thumbSig?: string | null;
  thumbX: number; // horizontal thumbnail position, 0=left … 50=center … 100=right
  categoryColors: Record<string, string>;
  description: { de: string; en: string };
  origin: { center?: [number, number]; zoom?: number };
  /** gebuchte Zusatzpakete — steuern Sichtbarkeit von Mängeln und KI */
  addons: Array<"maengel" | "ki" | "meldungen" | "pruefung" | "geraete" | "import">;
  /** Darf ich das Projekt verwalten? (Projektverwaltung, Issues #125/#129) */
  verwaltung?: boolean;
  /** eigene Rolle im Projekt (admin = globaler Admin); fehlt in Listenantworten */
  myRole?: "viewer" | "maengel" | "editor" | "admin";
  basemap: {
    options?: string[];
    defaultLight?: string;
    defaultDark?: string;
    /** 3D-Gelaende an/aus — weltweite Hoehen (Terrarium) unter /api/terrain/<projectId>/{z}/{x}/{y}; maxzoom ist historisch und wird nicht mehr gelesen */
    terrain?: { maxzoom?: number };
  };
  cardStats: CardStatConfig[];
  heroStats: CardStatConfig[];
  /** Anzahl Foto-Vorschauen im Karten-Tooltip (2 oder 4) */
  tooltipPhotos?: number;
  /** true when the project has its own geodata DB; false → uses the shared webgis_data */
  geoConnected: boolean;
  /** host:port/db of the project's own DB (no credentials), null when on the shared fallback.
   *  NUR fuer Admins im Antwortkoerper — sonst fehlt das Feld ganz. */
  geoHost?: string | null;
  /** parsed connection parts for prefilling the connection modal — NEVER includes the password.
   *  Ebenfalls nur fuer Admins; siehe pub() in projects.controller.ts. */
  geoConn?: { host: string; port: number; database: string; user: string; ssl: boolean } | null;
  /** admin-controlled Objektinfo (GFI): feature popup + hover highlight + click panel on the map */
  gfiEnabled: boolean;
  /** how a stepped (durchblättern) / selected feature is highlighted; null = teal default */
  highlight: { color: string; width: number } | null;
  sort: number;
  /** Logo der Kommune vorhanden — Adresse ueber logoAdresse() in lib/kommune.ts */
  logo?: boolean;
  /** Inhaltsstempel des Logos, als ?v= an die Logo-Adresse */
  logoSig?: string | null;
  /** Akzentfarbe der Oberflaeche (#rrggbb); null = Standard-Petrol */
  akzentfarbe?: string | null;
}

/** Projektmitglied, wie es die Mitglieder-Verwaltung liefert. */
export interface ApiProjectMember {
  userId: string;
  username: string;
  displayName: string;
  email: string;
  /** „verwaltung" = Editor mit Verwaltungsrecht */
  role: "viewer" | "maengel" | "editor" | "verwaltung";
}

/** Eintrag im Objekt-Verlauf (Audit, #19) — eine geaenderte Spalte je Zeile. */
export interface ApiFeatureChange {
  id: number;
  at: string;
  username: string;
  column: string;
  oldValue: string | null;
  newValue: string | null;
  /** app | qgis | qfield | import — woher die Aenderung kam */
  source: string;
  /** true: revert wuerde eine ZWISCHENZEITLICHE fremde Aenderung ueberschreiben */
  conflict?: boolean;
}

/** Admin-configured overview-card metric (a total aggregation + how to show it). */
export interface CardStatConfig {
  icon: string;
  label: string;
  unit?: string;
  agg: AggOp;
  layerId: string;
  field?: string | null;
  weightField?: string | null;
  filter?: AggFilter | null;
}

export interface ApiLayer {
  id: string;
  projectId: string;
  table: string;
  label: string;
  function: string;
  type: "polygon" | "point" | "line" | "raster";
  crs: string;
  sort: number;
  visibleDefault: boolean;
  style: Record<string, unknown>;
  defaultFilter: Record<string, unknown> | null;
  /** Wer zaehlt in die Versorgungsrechnung (Ortsteile-Reiter)? Blendet NICHTS auf der Karte aus. */
  metricFilter: { clauses?: Array<{ field: string; op: string; value?: string }>; combine?: "AND" | "OR"; note?: string } | null;
  idField: string;
  nameField: string | null;
  nrField: string | null;
  districtField: string | null;
  /** Spalte mit der Route des Kontrollgangs */
  routeField?: string | null;
  sizeField: string | null;
  noteField: string | null;
  tileProps: string[];
  /** true = Karte holt die Ebene als EIN GeoJSON statt als Kacheln */
  fullGeojson?: boolean;
  groupName: string | null;
  /** per-layer Objektinfo: popup + hover highlight + click panel (project gfiEnabled is the master) */
  gfiEnabled: boolean;
  /** layer offers a second, standardized "Mängel" form (defects + photos/docs) */
  maengelForm?: boolean;
}

export interface ApiStats {
  projectId: string;
  count: number;
  totalAreaM2: number;
  districts: Array<{ id: string; name: string; count: number; area: number; scores?: Record<string, number> }>;
  layers: Array<{ id: string; label: string; count: number }>;
  /** admin-configured overview-card metrics (computed); empty = use the built-in defaults */
  cards: Array<{ icon: string; label: string; unit: string; value: number | null }>;
  /** admin-configured big dashboard hero tiles (computed); empty = built-in defaults */
  hero: Array<{ icon: string; label: string; unit: string; value: number | null }>;
}

export interface ApiAttachment {
  id: string;
  layerId: string;
  featureId: string;
  kind: "photo" | "pdf" | "xlsx" | "other";
  filename: string;
  mime: string;
  size: number;
  meta: Record<string, unknown>;
  createdAt: string;
}

/**
 * Ein geloeschter Anhang, wie ihn der Papierkorb auflistet. Die Zeile in
 * `attachments` bleibt stehen und traegt nur `deleted_at`/`deleted_by` — deshalb
 * kennen wir hier noch Ebene, Objekt und Dateidaten. Die Datei selbst liegt
 * weiter in MinIO, wird aber nicht mehr ausgeliefert; erst das endgueltige
 * Loeschen (oder der naechtliche Lauf nach 30 Tagen) raeumt sie weg.
 */
export interface ApiTrashAttachment {
  id: string;
  layerId: string;
  layerLabel: string;
  /** null, wenn die Ebene weder lebt noch im Papierkorb liegt (Waise) — die Oberflaeche zeigt dann einen Strich. */
  projectId: string | null;
  featureId: string;
  filename: string;
  kind: ApiAttachment["kind"];
  mime: string;
  sizeBytes: number;
  deletedAt: string;
  /** Anzeigename der loeschenden Person; null, wenn das Konto inzwischen weg ist */
  deletedBy: string | null;
  uploadedAt: string;
}

/**
 * Eine geloeschte Ebene im Papierkorb. Der Server hat beim Loeschen alles, was per
 * ON DELETE CASCADE an der Ebene hing, in einen Schnappschuss gelegt; `counts` sagt,
 * wie viel darin steckt. `anhaenge` zaehlt die Anhaenge der Ebene, die NICHT im
 * Schnappschuss stehen — sie haengen an keinem Fremdschluessel und ueberleben ohnehin.
 */
export interface ApiTrashLayer {
  id: string;
  projectId: string;
  label: string;
  deletedAt: string;
  deletedBy: string | null;
  counts: { maengel: number; felder: number; formulare: number; filter: number; anhaenge: number };
}

/** Dringlichkeit als Frist (Konzept G4) — führend seit Migration 0044 */
export type MangelFrist = "sofort" | "kurzfristig" | "drei_monate" | "naechste_hu" | "beobachten";

export interface ApiMangel {
  id: string;
  layerId: string;
  featureId: string;
  category: string;
  inspector: string;
  description: string;
  /** Was zu TUN ist — getrennt von der Beschreibung des Schadens. */
  massnahme: string;
  /** Kostenansatz der Maßnahme in Euro; null = nicht geschätzt */
  kosten?: number | null;
  /** Haushaltsjahr, in dem die Maßnahme eingeplant ist */
  haushaltsjahr?: number | null;
  /** Stand der Maßnahme — vom Antrag bis zur Ausführung */
  massnahmeStatus?: "offen" | "beantragt" | "bewilligt" | "beauftragt" | "erledigt";
  severity: "gering" | "mittel" | "hoch" | "gefahr";
  /** fehlt bei älteren Servern — dann gilt die Zuordnung aus der Stufe (lib/frist.ts) */
  frist?: MangelFrist;
  status: "offen" | "in_arbeit" | "behoben" | "abgelehnt";
  foundAt: string | null;
  fixedAt: string | null;
  source: string | null;
  attachmentIds: string[];
  createdAt: string;
  updatedAt: string;
  /** gesetzt, wenn der Mangel aus einer Bürgermeldung übernommen wurde */
  meldungId?: string | null;
  /** zugeordnetes Spielgerät (Baustein Geräteverwaltung) */
  geraetId?: string | null;
}

export interface MaengelOptions {
  category: string[];
  inspector: string[];
}

/** Befund einer hochgeladenen Geodatei — die Datei bleibt 30 Min unter `token` gestaged. */
export interface LayerImportInspect {
  count: number;
  geomType: string;
  srid: number | null;
  /** Datei bringt ueberhaupt ein Koordinatensystem mit — auch eines ohne EPSG-Code */
  hasCrs: boolean;
  /** welche Zielspalten die Ebene hat — steuert, welche Zuordnung sichtbar ist */
  targets: { name: boolean; nr: boolean; art: boolean; size: boolean };
  fields: Array<{ name: string; type: string; sample: string[] }>;
  token: string;
}

/** Zuordnung fuer den Feature-Import; `artFixed` gilt fuer ALLE Zeilen statt der Spalte. */
export interface LayerImportBody {
  token: string;
  mapping: { name?: string | null; nr?: string | null; art?: string | null };
  artFixed?: string[] | null;
}

/**
 * Ein importierter Stichtag der Bevoelkerungsdaten — die Kopfzeile der
 * Historie. `einwohner`/`kinder` sind die Summen ueber alle Ortsteile des
 * Stichtags, `rows` die Zahl der Ortsteilzeilen.
 */
export interface ApiStichtag {
  /** ISO-Datum YYYY-MM-DD */
  stichtag: string;
  rows: number;
  einwohner: number | null;
  kinder: number | null;
  quelle: string | null;
  importedAt: string;
  /** Anzeigename der importierenden Person (nicht ihre Id) */
  importedBy: string | null;
}

/** Bevoelkerung EINES Ortsteils zu einem Stichtag; `baender` ist {"0-5":810,…}. */
export interface ApiPopRow {
  /** Name genau so, wie er im districtField der Objekte steht */
  ortsteil: string;
  einwohner: number | null;
  kinder: number | null;
  baender: Record<string, number>;
}

/** Befund einer hochgeladenen Bevoelkerungstabelle — 30 Min unter `token` gestaged. */
export interface ApiPopInspect {
  token: string;
  /** Datenzeilen in der Datei (ohne Kopfzeile) */
  count: number;
  /** Werteprobe JE SPALTE — entdoppelt, quer durch die Datei; daraus laesst sich keine Zeile bauen */
  fields: Array<{ name: string; sample: string[] }>;
  /**
   * Die ersten bis zu acht Datenzeilen, roh und zeilentreu: je Eintrag genau so
   * viele Elemente wie `fields`, in derselben Reihenfolge, leere Zellen als
   * leerer String. Fuer eine echte Zeilenvorschau IMMER das hier nehmen, nie
   * `fields[].sample` — das mischt Werte verschiedener Dateizeilen.
   */
  preview: string[][];
  /** Spaltenvorschlag des Servers; `baender` bildet Bandlabel -> Spaltenname ab */
  suggestion: { ortsteil: string | null; einwohner: string | null; kinder: string | null; baender: Record<string, string> };
  /** Abgleich mit den Ortsteilnamen aus den Objekten — nur fuer die Anzeige */
  match: { known: string[]; matched: string[]; unmatchedFile: string[]; missingKnown: string[] };
  stichtagGuess: string | null;
  /** Klartext, wenn der Abgleich nicht moeglich war (kein Ortsteilfeld o. ae.); sonst null */
  note: string | null;
}

/**
 * Uebernahme eines Stichtags. `baender` enthaelt genau die Baender, die als
 * Kinder zaehlen sollen — ohne `kinder`-Spalte bildet der Server ihre Summe.
 */
export interface ApiPopApplyBody {
  token: string;
  /** ISO-Datum YYYY-MM-DD */
  stichtag: string;
  quelle?: string | null;
  mapping: { ortsteil: string; einwohner?: string | null; kinder?: string | null; baender?: Record<string, string> };
}

export interface MaengelImportPreview {
  filename: string;
  /** how the file was read: deterministic Excel parse vs. LLM-extracted PDF */
  parser?: "xlsx" | "csv" | "pdf-llm";
  matchFieldDefault: string;
  sheets: Array<{
    name: string;
    headers: string[];
    rowCount: number;
    rows: Array<Record<string, string>>;
    suggested: Record<string, string | null>;
    /** confirmed mapping from a previous import of the same template (header signature) */
    savedMapping?: Record<string, string | null> | null;
  }>;
}

export interface MaengelImportMapping {
  sheet: string;
  matchField: string;
  matchCol: string;
  category?: string | null;
  description?: string | null;
  severity?: string | null;
  status?: string | null;
  foundAt?: string | null;
  /** Spalte mit dem Spielgerät (Baustein Geräteverwaltung) */
  geraet?: string | null;
  /** Spalte mit der prüfenden Person */
  pruefer?: string | null;
  /** Als Prüfung in die Kontrollhistorie eintragen (Baustein Spielplatzprüfung) */
  pruefart?: "routine" | "quartal" | "haupt" | "abnahme" | null;
}

export interface MaengelMatchCandidate {
  fid: string;
  label: string;
  score: number;
}
export interface MaengelMatchRow {
  i: number;
  key: string;
  category: string;
  description: string;
  severity: string | null;
  status: string | null;
  foundAt: string | null;
  geraet?: string;
  best: MaengelMatchCandidate | null;
  candidates: MaengelMatchCandidate[];
}
export interface MaengelMatchPreview {
  sheet: string;
  total: number;
  features: Array<{ fid: string; label: string }>;
  rows: MaengelMatchRow[];
}


/**
 * Welche Mängel die Auswertungen beschreiben. Etwas ANDERES als der
 * Objektfilter der Dashboard-Leiste: der grenzt die Spielplätze ein, die Sicht
 * die Mängel darauf. Zusammen ergibt das „Fallschutzmängel in Nordost, die
 * länger als ein Jahr offen sind" — ohne dass eine Achse von der anderen weiß.
 */
export interface MaengelSicht {
  status?: "offen" | "behoben" | null;
  severity?: "gering" | "mittel" | "hoch" | "gefahr" | null;
  alter?: "<30" | "30-90" | "90-365" | ">365" | null;
  kategorie?: string | null;
}

/** Kennzahlen und Auswertungen des Maengel-Reiters im Dashboard. */
/** Ein Mängel-Import der Ebene — Grundlage für „zurücknehmen" */
export interface ApiMaengelImport {
  id: string;
  filename: string | null;
  stats: { created?: number; updated?: number; unchanged?: number; vanished?: number; total?: number } | null;
  createdAt: string;
  undoneAt: string | null;
  createdBy: string | null;
}

/** Eine Zeile der Mängelliste im Mängel-Reiter */
export interface ApiMangelZeile {
  id: string;
  featureId: string;
  spielplatz: string;
  bezirk: string | null;
  kategorie: string;
  beschreibung: string;
  massnahme: string;
  dringlichkeit: "gering" | "mittel" | "hoch" | "gefahr";
  frist: MangelFrist;
  /** YYYY-MM-DD; null ohne festes Datum */
  zuBehebenBis: string | null;
  fristUeberschritten: boolean;
  status: "offen" | "in_arbeit" | "behoben" | "abgelehnt";
  /** Fundtag, sonst Anlagetag (YYYY-MM-DD) */
  erfasst: string | null;
  behoben: string | null;
  /** nur bei offenen Mängeln */
  offenSeitTage: number | null;
  pruefer: string;
  fotos: number;
  quelle: string | null;
  /** stadt: von der Kommune erfasst; meldung: aus einer Bürgermeldung */
  herkunft: "stadt" | "meldung";
  /** letzte Statusänderung: Anzeigename bzw. „System“ und Zeitpunkt (ISO) */
  statusGeaendertVon: string | null;
  statusGeaendertAm: string | null;
  /** zugeordnetes Spielgerät: Anzeigename und Kennung (fehlt bei älteren Servern) */
  geraet?: string | null;
  geraetId?: string | null;
}

/** Ein Eintrag im Statusverlauf eines Mangels */
export interface ApiStatusAenderung {
  statusAlt: string | null;
  statusNeu: string;
  von: string | null;
  am: string;
}
export interface MaengelStats {
  kennzahlen: {
    gesamt: number;
    /** Diese vier sind IMMER ungefiltert — die Kacheln sind die Navigation, nicht der Inhalt. */
    offen: number;
    gefahr: number;
    liegezeitSchnitt: number | null;
    behoben30: number;
    /** offene Mängel, die seit über einem Jahr liegen */
    altfaelle: number;
    objekteGesamt: number;
    /** Anzahl in der aktuell gewählten Sicht — darüber rechnen die Diagramme */
    inSicht: number;
    objekteBetroffen: number;
  };
  /** Objektkennungen der aktuellen Sicht (max. 400) — Grundlage für „Objekte filtern" */
  objekte: string[];
  jeBezirk: Array<{ name: string; gering: number; mittel: number; hoch: number; gefahr: number; total: number }>;
  jeKategorie: Array<{ name: string; n: number }>;
  liegezeit: Array<{ key: string; label: string; n: number }>;
  verlauf: Array<{ monat: string; neu: number; behoben: number }>;
}

/** Hinweisschicht „Handlungsbedarf" — je Fläche beim Lesen berechnet (Konzept G2). */
export type HinweisArt =
  | "mangel_gefahr"
  | "frist_ueberschritten"
  | "meldung_akut"
  | "meldung_neu"
  | "geraet_gesperrt"
  | "geraet_mangelhaft"
  | "geraet_ersatz"
  | "garantie_endet"
  | "pruefung_ueberfaellig"
  | "pruefung_faellig"
  | "nie_geprueft";
export interface HinweisGrund {
  art: HinweisArt;
  anzahl: number;
}
export interface HinweisEigenschaften {
  layerId: string;
  featureId: string;
  name: string | null;
  stufe: "warnung" | "hinweis";
  anzahl: number;
  gruende: HinweisGrund[];
}
export interface HinweisCollection {
  type: "FeatureCollection";
  features: Array<{ type: "Feature"; geometry: { type: "Point"; coordinates: [number, number] } | null; properties: HinweisEigenschaften }>;
}

/** Imbiss-Modus 🍔 (easter egg — trio only, endpoints 404 for everyone else) */
export interface OsmHit {
  osmId: string;
  name: string;
  gruppe: string;
  klasse: string | null;
  strasse: string | null;
  hausnummer: string | null;
  ort: string | null;
  lon: number;
  lat: number;
  imViewport: boolean;
}
export interface ImbissHit {
  osmId: string;
  name: string;
  lat: number;
  lon: number;
  cuisine: string | null;
  street: string | null;
  housenumber: string | null;
  city: string | null;
  openingHours: string | null;
  website: string | null;
  phone: string | null;
  stars: number | null;
}
export interface ImbissDetail extends ImbissHit {
  geometry: { type: string; coordinates: unknown } | null;
  images: Array<{ thumb: string; url: string; title: string }>;
  ratings: Array<{ username: string; field: string; value: number | null; value_text: string | null }>;
  averages: Record<string, number>;
  /** mindestens ein Haken „Veggie-Bonus" */
  veggie: boolean;
  /** wer da war: Bewertende plus deren „Dabei"-Eintraege, klein geschrieben */
  dabei: string[];
  /** alle drei, fuer die Auswahl „Dabei" */
  trio: string[];
}

export interface ApiUser {
  id: string;
  username: string;
  displayName: string;
  email: string;
  role: Role;
  /** Mitgliedschaften (Projekt + Rolle) — gepflegt wird ueber das Projekt */
  memberships: Array<{ projectId: string; role: ApiProjectMember["role"] }>;
  createdAt: string;
  /** Nachweis der Sachkunde für Prüfungen (Konzept E6); leer = keiner hinterlegt */
  sachkunde?: string;
  sachkundeBis?: string | null;
}

export interface FieldRange {
  min?: number | null;
  max?: number | null;
  step?: number | null;
}
export interface ApiColumn {
  name: string;
  dataType: string;
  nullable: boolean;
  alias: string | null;
  editable: boolean;
  input: "text" | "number" | "slider" | "dial" | null;
  decimals: number | null;
  config: FieldRange | null;
  hasRelation?: boolean;
  relationMulti?: boolean;
  /** Textspalte mit Mehrfachauswahl ("A | B" bzw. altes QField-Literal) — vom Server aus den Daten erkannt. */
  multi?: boolean;
}

/**
 * Die schlanke Spaltenzeile — Antwort von `GET /layers/:id/columns?slim=1`.
 *
 * Sie ist eine echte TEILMENGE von ApiColumn (deshalb Pick und keine eigene
 * Aufzaehlung: was der Server dort weglaesst, faellt hier automatisch mit weg,
 * wenn ApiColumn sich aendert). Wer damit arbeitet, bekommt vom Compiler
 * gesagt, dass `config`, `input`, `decimals`, `editable` und `nullable` NICHT
 * da sind — genau das soll er merken, bevor er sie stillschweigend als
 * undefined liest.
 */
export type ApiColumnSlim = Pick<ApiColumn, "name" | "dataType" | "alias" | "multi">;

export interface ApiSavedChart {
  id: string;
  projectId: string;
  name: string;
  chartType: "bar" | "hbar" | "line" | "area" | "donut" | "radar";
  metrics: Array<"count" | "area" | "mpk">;
  districtIds: string[] | null;
  spec: ChartSpec | null;
  createdBy: string | null;
  shared: boolean;
  createdAt: string;
}

/**
 * Gespeicherter Filter eines Projekts. `filter` traegt die Klauseln, wie der
 * Server sie ausfuehrt — plus optional `dash`: die Arbeitskopie der
 * Dashboard-Filterleiste (Schnellauswahlen + freie Bedingungen), damit ein
 * geladener Filter dieselben Chips zeigt, aus denen er gespeichert wurde.
 */
export interface ApiSavedFilter {
  id: string;
  projectId?: string;
  layerId: string;
  name: string;
  filter: { clauses: Array<{ field: string; op: string; value: string }>; combine: "AND" | "OR"; dash?: unknown };
  shared: boolean;
  createdBy?: string | null;
  createdAt?: string;
  /** zuletzt geladen — sortiert „Zuletzt genutzt"; aeltere Server liefern es nicht */
  usedAt?: string | null;
  /** letzte inhaltliche Aenderung (PATCH) — aeltere Server liefern es nicht */
  updatedAt?: string | null;
}

export interface ApiConnTest {
  ok: boolean;
  message: string;
  layers: Array<{ name: string; type: string; crs: string; rows: number }>;
}

export interface MapViewState {
  basemap?: string | null;
  order?: string[];
  groupVis?: Record<string, boolean>;
  filters?: Record<string, unknown>;
  layers?: Record<string, { visible?: boolean; groupName?: string | null; style?: Record<string, unknown>; simple?: { stroke: string; fill: string; fillOpacity: number } }>;
  // a theme can drive the map view (zoom to a district / area) — captured on save, flown to on load
  camera?: { center: [number, number]; zoom: number };
  // per-theme GFI: which named form profile each layer shows while this theme is active
  gfi?: Record<string, string>;
}

export interface ApiMapView {
  id: string;
  projectId: string;
  name: string;
  state: MapViewState;
  createdBy: string | null;
  shared: boolean;
  createdAt: string;
}

export interface SearchHit {
  layerId: string;
  layerLabel: string;
  geomType: string;
  fid: string;
  name: string | null;
  nr: string | null;
  district: string | null;
  bbox: [number, number, number, number];
  center: [number, number];
  /**
   * Gesetzt, wenn das Objekt ueber die BILDUNTERSCHRIFT eines Fotos gefunden
   * wurde und nicht ueber Name/Nummer/Ortsteil. Die Unterschrift stammt aus den
   * Metadaten der Begehungsfotos und beschreibt, was zu sehen ist — damit findet
   * die Suche Ausstattung, fuer die es keine Spalte gibt („Bodentrampolin").
   * Solche Treffer stehen immer HINTER den Namenstreffern.
   */
  photoId?: string;
  caption?: string;
  takenAt?: string | null;
}

export interface WmsNode {
  name: string | null;
  title: string;
  crs: string[];
  children: WmsNode[];
}

export interface WfsFeatureType {
  name: string;
  title: string;
  crs: string;
}

/** Art eines Layers aus einer umgewandelten CAD-Zeichnung (dwg2gis: <praefix>_polygon|line|text|point|block). */
export type ImportCadKind = "polygon" | "line" | "text" | "point" | "block";
/** Ein Layer einer hochgeladenen Datei, wie /imports/inspect ihn meldet. */
export interface ImportFileLayer {
  name: string;
  crs: string | null;
  geom: string;
  count: number;
  /** nur bei DWG/DXF gesetzt — damit der Dialog „Flächen", „Linien" … statt „plan_polygon" zeigen kann */
  cad?: ImportCadKind | null;
}

export interface ImportJob {
  id: string;
  projectId: string | null;
  kind: "wfs" | "file";
  source: string;
  typeName: string | null;
  targetTable: string;
  label?: string | null;
  status: "queued" | "running" | "done" | "error";
  step: string | null;
  detail: string | null;
  progress: number;
  featureCount: number | null;
  layerId: string | null;
  error: string | null;
  queuePosition?: number;
  createdAt: string;
  finishedAt: string | null;
}

export interface FormCond {
  field: string;
  op: "contains" | "=" | "!=" | ">=" | "<=" | ">" | "<" | "notnull";
  value?: string;
}
export type FormGroupCondition = FormCond | { all: FormCond[] };
export interface FieldDisplay {
  variant: "traffic" | "chip" | "stars";
  min?: number;
  max?: number;
  /** traffic: by default LOW value = green (grades); invert flips to HIGH = green */
  invert?: boolean;
}

export interface FormLayoutDto {
  tabs: Record<string, Array<{ id: string; title: string; fields: string[]; visibleIf?: FormGroupCondition | null }>>;
  meta: {
    titles: Record<string, string>;
    labels: Record<string, string>;
    order?: string[];
    tabVisibleIf?: Record<string, FormGroupCondition>;
    /** styled forms: per-field display variant in the GFI drawer */
    display?: Record<string, FieldDisplay>;
    /** Anhänge & Mängel placement: undefined = every tab, '<tabId>' = only there, 'none' = hidden */
    attachmentsTab?: string;
  };
}

// ---- client ----

// ---- Buergermeldungen (Konzept §3) ----
export type MeldungStatus = "neu" | "in_pruefung" | "mangel" | "erledigt" | "nicht_zutreffend";
export type MeldungZuordnung = "qr" | "buerger" | "gps" | "ki" | "hand";
export type MeldungKanal = "qr" | "formular" | "mail" | "intern" | "schnittstelle";

/** Meldungstyp der zweistufigen Typologie: Typ mit Symbol, darunter Unterarten */
export interface ApiMeldungsTyp {
  name: string;
  /** Phosphor-Name, z. B. "wrench" */
  symbol: string;
  beschreibung: string;
  unterarten: string[];
}

/** Vorschlag der KI fuer eine Meldung ohne sichere Zuordnung */
export interface ApiKiVorschlag {
  layerId: string;
  featureId: string;
  name: string | null;
  /** 0..1 */
  sicherheit: number;
  begruendung: string;
  kategorie?: string | null;
}

export interface ApiMeldung {
  id: string;
  nr: number;
  /** „M-00042" */
  vorgang: string;
  layerId: string | null;
  featureId: string | null;
  flaecheName: string | null;
  zuordnung: MeldungZuordnung | null;
  kiVorschlag: ApiKiVorschlag | null;
  kanal: MeldungKanal;
  /** Herkunft aus einem Fremdsystem (Schnittstelle, Issue #130) */
  fremdQuelle?: string | null;
  fremdId?: string | null;
  /** Meldungstyp (erste Ebene) */
  kategorie: string;
  unterart: string;
  text: string;
  ort: string;
  lon: number | null;
  lat: number | null;
  akut: boolean;
  /** nur fuer Rollen, die bearbeiten duerfen — sonst null */
  kontaktName: string | null;
  kontaktMail: string | null;
  status: MeldungStatus;
  begruendung: string;
  bearbeitetVon: string | null;
  bearbeitetAt: string | null;
  abgeschlossenAt: string | null;
  eingangAt: string;
  /** bei interner Erfassung: wer erfasst hat */
  erfasstVon: string | null;
  fotos: number;
  mangelId: string | null;
  /** zugeordnetes Spielgerät (fehlt bei älteren Servern) */
  geraetId?: string | null;
  geraet?: string | null;
}

export interface ApiMeldungenListe {
  meldungen: ApiMeldung[];
  typologie: ApiMeldungsTyp[];
  kategorien: string[];
  mitKontakt: boolean;
}

export interface ApiSpielflaeche {
  layerId: string;
  featureId: string;
  name: string;
}

export interface ApiMeldeportal {
  kennung: string;
  aktiv: boolean;
  akutMail: string;
  typologie: ApiMeldungsTyp[];
}

/** Daten fuer das Meldeformular — oeffentlich (mit Zeitmarke) und im Meldeportal der Anwendung */
export interface ApiMeldeSeite {
  projekt: string;
  /** Logo der Kommune unter /api/melden/:kennung/logo */
  logo?: boolean;
  akzentfarbe?: string | null;
  typologie: ApiMeldungsTyp[];
  flaechen: ApiSpielflaeche[];
  marke?: string;
}

/** Inhalt einer Meldung, wie das Formular ihn erfasst */
export interface ApiMeldungInhalt {
  kategorie: string;
  unterart: string;
  text: string;
  ort: string;
  akut: boolean;
  layerId: string | null;
  featureId: string | null;
  lon: number | null;
  lat: number | null;
  kontaktName: string;
  kontaktMail: string;
  fotos: string[];
  /** Spielgerät der gewählten Fläche (Baustein Geräteverwaltung) */
  geraetId?: string | null;
}

export interface ApiMeldungSenden extends ApiMeldungInhalt {
  marke: string;
  website: string;
  ueberQr: boolean;
  /** Cloudflare-Turnstile-Token. Nur der oeffentliche Weg fuehrt ihn. */
  token: string;
}

// ---- Funktionswünsche ----
export type WunschStatus = "offen" | "geplant" | "in_arbeit" | "umgesetzt" | "abgelehnt";

export interface ApiWunsch {
  id: string;
  titel: string;
  text: string;
  status: WunschStatus;
  /** Anmerkung von LILASp, für alle sichtbar */
  adminNotiz: string;
  erstelltAt: string;
  erstelltVon: string | null;
  /** selbst eingereicht — darf gelöscht werden */
  eigener: boolean;
  hoch: number;
  runter: number;
  punkte: number;
  meineStimme: -1 | 0 | 1;
}

// ---- Geräteverwaltung ----
export type GeraetZustand = "gut" | "befriedigend" | "mangelhaft" | "gesperrt" | "unbekannt";

export interface ApiGeraet {
  id: string;
  layerId: string;
  featureId: string;
  /** Kennung der Geräteart (lib/geraete.ts) */
  art: string;
  artName: string;
  bezeichnung: string;
  inventarnummer: string;
  hersteller: string;
  modell: string;
  baujahr: number | null;
  /** volle Jahre seit dem Baujahr */
  alter: number | null;
  zustand: GeraetZustand;
  fallschutz: string;
  norm: string;
  anzahl: number;
  notiz: string;
  /** YYYY-MM-DD; gesetzt = abgebaut, bleibt im Verzeichnis */
  abgebautAm: string | null;
  offeneMaengel: number;
  maengel: number;
  createdAt: string;
  updatedAt: string;
  /** Angaben aus der Spielplatzakte (fehlen bei älteren Servern) */
  aufbaufirma?: string;
  garantieBis?: string | null;
  garantieEndetBald?: boolean;
  /** abweichende Nutzungsdauer am Gerät; null = Kennwert bzw. Richtwert */
  nutzungsdauerJahre?: number | null;
  /** wirksame Nutzungsdauer in Jahren */
  nutzungsdauer?: number;
  ersatzjahr?: number | null;
  /** abweichende Ersatzkosten je Stück am Gerät */
  ersatzkosten?: number | null;
  /** wirksame Ersatzkosten je Stück; null = nicht hinterlegt */
  ersatzkostenWirksam?: number | null;
  /** Verweise auf Fotos (Anhänge der Fläche) */
  fotoIds?: string[];
  /** Sperrvermerk, solange der Zustand „gesperrt“ ist */
  gesperrtSeit?: string | null;
  gesperrtGrund?: string;
  gesperrtVon?: string | null;
}

export interface ApiGeraetZeile extends ApiGeraet {
  ebene: string;
  spielplatz: string;
  bezirk: string | null;
}

export type ApiGeraetEingabe = Partial<
  Pick<ApiGeraet, "art" | "bezeichnung" | "inventarnummer" | "hersteller" | "modell" | "aufbaufirma" | "baujahr" | "zustand" | "fallschutz" | "norm" | "anzahl" | "notiz" | "abgebautAm" | "garantieBis" | "nutzungsdauerJahre" | "ersatzkosten" | "fotoIds" | "gesperrtGrund">
>;

export interface GeraeteImportVorschau {
  filename: string;
  matchFieldDefault: string;
  sheets: Array<{ name: string; headers: string[]; rowCount: number; rows: Array<Record<string, string>>; suggested: Record<string, string | null> }>;
}

export type GeraeteImportMapping = { sheet: string; matchField: string; matchCol: string } & Record<string, string | null>;

export interface GeraeteImportZeile {
  i: number;
  key: string;
  art: string | null;
  artName: string | null;
  bezeichnung: string | null;
  inventarnummer: string | null;
  baujahr: number | null;
  zustand: GeraetZustand | null;
  best: { fid: string; label: string; score: number } | null;
  candidates: Array<{ fid: string; label: string; score: number }>;
}

/** Eigene Erinnerungsmail „Handlungsbedarf" je Projekt */
export interface ApiErinnerung {
  aktiv: boolean;
  rhythmus: "taeglich" | "woechentlich";
  email: string;
  letzteAt: string | null;
  /** Server hat einen Mailversand */
  mailBereit: boolean;
}

// ---- Routen (Issues #122–#124) ----
export type RoutenArt = "frei" | "fest" | "tag";
export type RoutenGrundlage = "wegenetz" | "luftlinie";
export interface ApiRoutenPunkt {
  lon: number;
  lat: number;
}
export interface ApiRoutenHalt {
  layerId: string;
  featureId: string;
  name: string;
  ortsteil: string | null;
  lon: number | null;
  lat: number | null;
  /** Die Fläche gibt es nicht mehr */
  fehlt: boolean;
  /** Prüfstand, wenn die Spielplatzprüfung gebucht ist */
  pruefung: PruefArtStatus | null;
}
export interface ApiRoute {
  id: string;
  name: string;
  art: RoutenArt;
  /** 1 = Montag … 7 = Sonntag */
  wochentag: number | null;
  datum: string | null;
  team: string;
  notiz: string;
  start: ApiRoutenPunkt | null;
  laengeM: number | null;
  grundlage: RoutenGrundlage | null;
  personen: Array<{ id: string; name: string }>;
  /** Ich stehe auf der Route — oder jemand, den ich gerade vertrete */
  meine: boolean;
  /** Personen der Route, die ich gerade vertrete */
  vertreten: string[];
  updatedAt: string;
  halte: ApiRoutenHalt[];
}
export interface ApiRoutenStandort {
  layerId: string;
  featureId: string;
  name: string;
  ortsteil: string | null;
  lon: number;
  lat: number;
  pruefung: PruefArtStatus | null;
}
export interface ApiRoutenListe {
  routen: ApiRoute[];
  standorte: ApiRoutenStandort[];
  ortsteile: string[];
  personen: Array<{ id: string; name: string }>;
  /** Prüfstand verfügbar (Baustein gebucht) */
  pruefung: boolean;
}
export interface ApiRoutenEingabe {
  name: string;
  art: RoutenArt;
  wochentag?: number | null;
  datum?: string | null;
  team: string;
  notiz: string;
  start?: ApiRoutenPunkt | null;
  personen: string[];
  halte: Array<{ layerId: string; featureId: string }>;
}
export interface ApiRoutenReihenfolge {
  halte: Array<{ layerId: string; featureId: string }>;
  laengeM: number;
  grundlage: RoutenGrundlage;
}
export interface ApiRoutenPlan {
  tage: Array<{ halte: Array<{ layerId: string; featureId: string; name: string; ortsteil: string | null }>; ortsteile: string[]; laengeM: number; grundlage: RoutenGrundlage | null }>;
  grundlage: RoutenGrundlage | null;
  anzahl: number;
}
export interface ApiRoutenImport {
  name: string;
  punkte: number;
  zugeordnet: Array<{ layerId: string; featureId: string; name: string; ausDatei: string; abstandM: number | null; ueber: "ort" | "name" | null }>;
  offen: Array<{ name: string; lon: number; lat: number }>;
}
export type RoutenFormat = "gpx" | "kml" | "kmz" | "geojson" | "csv";

// ---- Bericht für die Dienstbesprechung (Issue #131) ----
export type BerichtTeil = "maengel" | "pruefungen" | "meldungen" | "protokoll";
export interface ApiBericht {
  projekt: string;
  von: string;
  bis: string;
  ortsteil: string | null;
  teile: BerichtTeil[];
  ausgelassen: Array<{ teil: BerichtTeil; grund: string }>;
  maengel?: {
    neu: number;
    behoben: number;
    offen: number;
    gefahrOffen: number;
    kostenOffen: number;
    nachKategorie: Array<{ name: string; neu: number; behoben: number }>;
    liste: Array<{ flaeche: string; ortsteil: string | null; kategorie: string; beschreibung: string; stufe: string; status: string; gefunden: string | null; behoben: string | null; kosten: number | null }>;
  };
  pruefungen?: {
    abgeschlossen: number;
    mitBefund: number;
    nachArt: Array<{ art: string; name: string; anzahl: number }>;
    liste: Array<{ flaeche: string; ortsteil: string | null; art: string; datum: string; pruefer: string; ergebnis: string | null }>;
  };
  meldungen?: {
    eingegangen: number;
    abgeschlossen: number;
    offen: number;
    akut: number;
    nachKategorie: Array<{ name: string; anzahl: number }>;
    liste: Array<{ vorgang: string; flaeche: string | null; ortsteil: string | null; kategorie: string; status: string; eingang: string; abschluss: string | null; akut: boolean }>;
  };
  protokoll?: {
    aenderungen: number;
    nachEbene: Array<{ ebene: string; anzahl: number }>;
    nachPerson: Array<{ person: string; anzahl: number }>;
    liste: Array<{ am: string; ebene: string; objekt: string; aktion: string; person: string; felder: string }>;
    ohneProtokoll: string[];
  };
}
export interface ApiBerichtAbo {
  id: string;
  name: string;
  wochentag: number;
  tage: number;
  teile: BerichtTeil[];
  ortsteil: string | null;
  empfaenger: string[];
  letzteAt: string | null;
}
type BerichtAnfrage = { von: string; bis: string; teile: BerichtTeil[]; ortsteil: string | null };
const berichtQuery = (a: BerichtAnfrage) =>
  `von=${a.von}&bis=${a.bis}&teile=${a.teile.join(",")}${a.ortsteil ? `&ortsteil=${encodeURIComponent(a.ortsteil)}` : ""}`;

// ---- Lizenzen (Issue #126) ----
export type LizenzHinweis = "abgelaufen" | "bald" | null;
export interface ApiLizenz {
  projectId: string;
  name: string;
  paket: string;
  /** null = unbegrenzt */
  plaetze: number | null;
  belegt: number;
  bis: string | null;
  notiz: string;
  hinweis: LizenzHinweis;
  bausteine: Array<{ addon: string; name: string; seit: string | null; bis: string | null; notiz: string; hinweis: LizenzHinweis }>;
  verlauf?: Array<{ addon: string; name: string; aktion: string; am: string; von: string | null }>;
}

// ---- Zuständigkeiten, Benachrichtigungen, Vertretung (Issue #134) ----
export type ZustaendigArt = "projekt" | "ortsteil" | "flaeche";
export interface ApiZustaendigkeit {
  id: string;
  art: ZustaendigArt;
  ortsteil: string | null;
  layerId: string | null;
  featureId: string | null;
  flaeche: string | null;
  userId: string | null;
  person: string | null;
  vertretung: string | null;
  adresse: string | null;
}
export interface ApiZustaendigkeitEingabe {
  gebiet: { art: "projekt" } | { art: "ortsteil"; ortsteil: string } | { art: "flaeche"; layerId: string; featureId: string };
  userId?: string | null;
  adresse?: string | null;
}
export interface ApiBenachrichtigungen {
  einstellung: { aktiv: boolean; ereignisse: string[] };
  ereignisse: Array<{ id: string; name: string }>;
  zustaendigkeiten: ApiZustaendigkeit[];
  protokoll: Array<{ ereignis: string; empfaenger: string; ok: boolean; am: string }>;
}
export interface ApiVertretung {
  vertretung: { id: string; name: string; seit: string } | null;
  vertrete: Array<{ id: string; name: string; seit: string }>;
  kandidaten: Array<{ id: string; name: string }>;
}

// ---- Spielplatzprüfung ----
export type Pruefart = "routine" | "quartal" | "haupt" | "abnahme";
export type PruefPlanart = Exclude<Pruefart, "abnahme">;
export type PruefArtStatus = "ok" | "faellig" | "ueberfaellig" | "nie" | "aus";
export type PruefErgebnis = "ohne_befund" | "maengel" | "sperren" | "freigegeben" | "auflagen" | "nicht_freigegeben";
export type PruefPunktErgebnis = "ok" | "mangel" | "nicht_pruefbar";

export interface ApiPruefPunkt {
  key: string;
  typ: "platz" | "geraet" | "nachkontrolle";
  titel: string;
  kategorie?: string;
  geraetId?: string | null;
  mangelId?: string | null;
  /** Nachkontrolle: ok = behoben, mangel = weiterhin offen */
  ergebnis: PruefPunktErgebnis | null;
  befund?: string;
  frist?: MangelFrist | null;
  sperren?: boolean;
  zustand?: GeraetZustand | null;
  fotoIds?: string[];
}

export interface ApiPruefung {
  id: string;
  layerId: string;
  featureId: string;
  art: Pruefart;
  artName: string;
  datum: string;
  pruefer: string;
  firma: string;
  ergebnis: PruefErgebnis | null;
  bemerkung: string;
  punkte: ApiPruefPunkt[];
  basisPruefungId: string | null;
  quelle: "protokoll" | "import" | "akte";
  /** gesetzt = abgeschlossen und unveränderlich */
  abgeschlossenAt: string | null;
  abgeschlossenVon: string | null;
  createdAt: string;
  updatedAt: string;
  /** Mängel, die aus diesem Protokoll entstanden sind */
  maengel: number;
  vorschlag: PruefErgebnis;
  /** nur in der Antwort auf „abschließen" */
  angelegteMaengel?: number;
  behobeneMaengel?: number;
}

export interface ApiPruefIntervall {
  art: PruefPlanart;
  intervallTage: number;
  vorlaufTage: number;
  ueberwacht: boolean;
}

export interface ApiPruefArtLage {
  art: PruefPlanart;
  status: PruefArtStatus;
  letzte: string | null;
  faelligAm: string | null;
  /** negativ = überfällig */
  tageBis: number | null;
  intervallTage: number;
  ueberwacht: boolean;
}

export interface ApiPruefFlaeche {
  layerId: string;
  featureId: string;
  name: string;
  /** Route des Kontrollgangs, wenn die Ebene eine führt */
  route?: string | null;
  arten: ApiPruefArtLage[];
  abnahme: string | null;
  entwuerfe: number;
  pruefungenJahr: number;
  status: PruefArtStatus;
}

export interface ApiPruefKennzahlen {
  /** Anteil der Spielplätze mit gültiger Jahreshauptinspektion, in Prozent */
  erfuellung: number | null;
  erfuellungBasis: number;
  /** mittlere Tage bis zur Behebung, letzte zwölf Monate */
  behebungsdauer: number | null;
  behobene: number;
  jeArt: Array<{ art: string; anzahl: number }>;
  jeHersteller: Array<{ hersteller: string; anzahl: number; geraete: number }>;
  jeHaushaltsjahr: Array<{ jahr: number; kosten: number; anzahl: number; ohneKosten: number }>;
}

/** Eine Spielplatzakte, wie sie das Sammel-PDF braucht */
export interface ApiAkte {
  layerId: string;
  featureId: string;
  name: string;
  route: string | null;
  arten: ApiPruefArtLage[];
  abnahme: string | null;
  geraete: ApiGeraet[];
  maengel: ApiMangel[];
  pruefungen: ApiPruefung[];
}

export interface ApiPruefUebersicht {
  intervalle: ApiPruefIntervall[];
  kennzahlen: ApiPruefKennzahlen;
  flaechen: ApiPruefFlaeche[];
  statistik: { flaechen: number; ueberfaellig: number; faellig: number; nie: number; aktuell: number; pruefungenJahr: number; entwuerfe: number };
  zuletzt: Array<ApiPruefung & { name: string }>;
}

export interface ApiPruefVorlage {
  art: Pruefart;
  artName: string;
  /** true = die Kommune hat eigene Punkte hinterlegt */
  eigen: boolean;
  punkte: Array<{ key: string; text: string; kategorie: string }>;
  /** Vorgabe des Systems, für „zurücksetzen" */
  vorgabe: Array<{ key: string; text: string; kategorie: string }>;
}

export interface ApiPruefFlaechenDetail {
  intervalle: ApiPruefIntervall[];
  plan: Array<{ art: PruefPlanart; intervallTage: number | null; aktiv: boolean }>;
  arten: ApiPruefArtLage[];
  abnahme: string | null;
  /** Sachkunde der angemeldeten Person — ohne sie bleibt der Abschluss der Fachprüfungen gesperrt */
  sachkunde: { nachweis: boolean; bis: string | null };
  pruefungen: ApiPruefung[];
}

export interface ApiPlanungsSumme {
  anzahl: number;
  kosten: number;
  ohneKosten: number;
}

export interface ApiErsatzplanung {
  abJahr: number;
  faellig: ApiPlanungsSumme;
  jahre: Array<ApiPlanungsSumme & { jahr: number }>;
  naechsteFuenfJahre: ApiPlanungsSumme;
  ohneBaujahr: number;
}

export interface ApiGeraeteKennwert {
  art: string;
  name: string;
  richtwertJahre: number;
  nutzungsdauerJahre: number | null;
  ersatzkosten: number | null;
}

export interface ApiGeraeteStatistik {
  geraete: number;
  eintraege: number;
  spielplaetze: number;
  durchschnittsalter: number | null;
  mangelhaftOderGesperrt: number;
  mitOffenenMaengeln: number;
  offeneMaengel: number;
  jeZustand: Record<GeraetZustand, number>;
  jeAlter: Array<{ klasse: "0-5" | "6-10" | "11-15" | "16-20" | ">20" | "unbekannt"; label: string; anzahl: number }>;
  jeArt: Array<{ art: string; name: string; anzahl: number; spielplaetze: number; durchschnittsalter: number | null; zustand: Record<GeraetZustand, number>; offeneMaengel: number }>;
  ersatzplanung?: ApiErsatzplanung;
  garantieEndetBald?: number;
}

export interface UebersichtCollection {
  type: "FeatureCollection";
  features: Array<{
    type: "Feature";
    geometry: unknown;
    properties: { projekt: string; projektName: string; layerId: string; name: string | null; fid?: string };
  }>;
  /** Projekte, deren Geo-DB nicht antwortete. */
  fehlend: Array<{ projekt: string; fehler: string }>;
}

export const http: ApiClient & {
  me(): Promise<SessionUser | null>;
  /** currentPassword ist Pflicht, sobald `username` oder `email` wechseln —
   *  beides sind Anmeldemerkmale (siehe auth.controller.ts, updateAccount). */
  account(update: Partial<Pick<SessionUser, "username" | "displayName" | "email" | "photoUrl" | "language">> & { currentPassword?: string }): Promise<SessionUser>;
  changePassword(currentPassword: string, newPassword: string): Promise<void>;
  /** Admin-Gesamtkarte: Grenzen/Spielplaetze aller aktiven Projekte als ein GeoJSON. */
  uebersicht: {
    grenzen(): Promise<UebersichtCollection>;
    spielplaetze(): Promise<UebersichtCollection>;
  };
  projects: {
    list(): Promise<ApiProject[]>;
    get(id: string): Promise<ApiProject>;
    update(id: string, patchBody: Record<string, unknown>): Promise<ApiProject>;
    remove(id: string): Promise<void>;
    /** test + persist this project's own geodata DB connection (built from discrete fields, never echoed back) */
    setGeoConnection(id: string, body: { host: string; port: number; database: string; user: string; password?: string; ssl?: boolean }): Promise<ApiProject & { tables: number }>;
    /** disconnect → falls back to the shared webgis_data */
    clearGeoConnection(id: string): Promise<ApiProject>;
    stats(id: string): Promise<ApiStats>;
    members: {
      list(id: string): Promise<ApiProjectMember[]>;
      /** legt an ODER aendert — genau eine Rolle je Nutzer+Projekt */
      set(id: string, userId: string, role: ApiProjectMember["role"]): Promise<void>;
      remove(id: string, userId: string): Promise<void>;
    };
    setAddons(id: string, addons: ApiProject["addons"]): Promise<ApiProject>;
    /** Kategorienfarben der Diagramme — ab Projektrolle editor (Issue #127) */
    setCategoryColors(id: string, farben: Record<string, string>): Promise<{ categoryColors: Record<string, string> }>;
    aggregate(id: string, query: AggQuery): Promise<AggResult>;
    maengelStats(id: string, body: { layerId: string; filter?: AggFilter | null; sicht?: MaengelSicht | null }): Promise<MaengelStats>;
    /** Maengeltabelle als Excel — gleiche Eingrenzung wie maengelStats */
    maengelTabelle(id: string, body: { layerId: string; filter?: AggFilter | null; sicht?: MaengelSicht | null }): Promise<{ blob: Blob; dateiname: string | null }>;
    /** alle Mängel der Ebene als Liste — eingegrenzt nur über den Objektfilter */
    maengelListe(id: string, body: { layerId: string; filter?: AggFilter | null }): Promise<{ gefiltert: boolean; zeilen: ApiMangelZeile[] }>;
    /** Status mehrerer Mängel auf einmal setzen — jede Änderung landet im Statusverlauf */
    maengelStatusSetzen(id: string, body: { layerId: string; ids: string[]; status: ApiMangelZeile["status"] }): Promise<{ geaendert: number; angefragt: number }>;
    hinweise(id: string): Promise<HinweisCollection>;
    layers(id: string): Promise<ApiLayer[]>;
    /** free-text feature search across the project's layers → hits with bbox + inside-point */
    search(id: string, q: string): Promise<SearchHit[]>;
    /** Nur die Fotosuche — fuer gezielte Fragen nach Bildinhalten. */
    photoSearch(id: string, q: string, limit?: number): Promise<SearchHit[]>;
    /** zuletzt bearbeitete Objekte (Vorschlaege der Karten-Suche) */
    recent(id: string): Promise<Array<SearchHit & { at: string }>>;
    charts: {
      list(projectId: string): Promise<ApiSavedChart[]>;
      create(projectId: string, body: Record<string, unknown>): Promise<ApiSavedChart>;
      update(projectId: string, id: string, body: Record<string, unknown>): Promise<ApiSavedChart>;
      remove(projectId: string, id: string): Promise<void>;
    };
    filters: {
      list(projectId: string): Promise<ApiSavedFilter[]>;
      create(projectId: string, body: Record<string, unknown>): Promise<ApiSavedFilter>;
      /** Name, Klauseln, Sichtbarkeit — NICHT `usedAt`, das laeuft ueber used(). */
      update(projectId: string, id: string, body: Record<string, unknown>): Promise<ApiSavedFilter>;
      /** „benutzt" melden (Betrachten IST Benutzen) — setzt used_at serverseitig. */
      used(projectId: string, id: string): Promise<ApiSavedFilter>;
      remove(projectId: string, id: string): Promise<void>;
    };
    mapViews: {
      list(projectId: string): Promise<ApiMapView[]>;
      create(projectId: string, body: { name: string; state: MapViewState }): Promise<ApiMapView>;
      rename(projectId: string, id: string, name: string): Promise<ApiMapView>;
      setGfi(projectId: string, id: string, layerId: string, profile: string | null): Promise<ApiMapView>;
      remove(projectId: string, id: string): Promise<void>;
    };
    /** Bevoelkerung je Ortsteil: Historie lesen, Tabelle hochladen, Stichtag setzen */
    population: {
      stichtage(projectId: string): Promise<ApiStichtag[]>;
      /** ohne `stichtag` der neueste vorhandene; nichts importiert -> { stichtag: null, rows: [] } */
      rows(projectId: string, stichtag?: string): Promise<{ stichtag: string | null; rows: ApiPopRow[] }>;
      /** Schritt 1: Datei pruefen und stagen (Editorin/Admin) */
      inspect(projectId: string, file: File): Promise<ApiPopInspect>;
      /** Schritt 2: uebernehmen — ersetzt den Stichtag komplett; `unmatched` nennt die uebergangenen Zeilen
       *  (hoechstens die ersten 200), `unmatchedTotal` sagt, wie viele es wirklich waren */
      apply(projectId: string, body: ApiPopApplyBody): Promise<{ stichtag: string; written: number; unmatched: string[]; unmatchedTotal?: number }>;
      remove(projectId: string, stichtag: string): Promise<{ deleted: number }>;
    };
  };
  layers: {
    update(id: string, body: Record<string, unknown>): Promise<ApiLayer>;
    remove(id: string): Promise<void>;
    /**
     * Spaltenkatalog eines Layers — voll oder schlank.
     *
     * `{ slim: true }` holt nur { name, dataType, alias, multi }. Gemessen fuer
     * Wiesbaden: 416 Eintraege / 99 138 B roh auf der Landeansicht, 254 ms auf
     * gebremstem Netz — und gebraucht werden dort genau drei Dinge: die
     * Zuordnung name→alias, das Erkennen der Kategoriespalten und die Feldliste
     * der Filterleiste. Ohne den Parameter bleibt die Antwort unveraendert.
     *
     * ⚠ VERSCHIEDENE Query-Keys, sonst ist es ein stiller Datenverlust — den
     * Key deshalb NIE von Hand schreiben, sondern immer ueber `spaltenKey()`:
     *   spaltenKey(layerId)                → ["columns", layerId]
     *                                        (Formulareditor, Objektliste,
     *                                         SQL-Werkzeuge — brauchen
     *                                         config/input/decimals)
     *   spaltenKey(layerId, { slim: true })→ ["columns", layerId, "slim"]
     *                                        (Landeansicht, Filterleiste)
     * Unter EINEM Schluessel wuerde der Formulareditor die schlanke Antwort aus
     * dem Cache bekommen und Wertebeziehungen, Eingabeart und Nachkommastellen
     * verlieren — sichtbar erst, wenn jemand ein Formular speichert.
     * Der ETag-Speicher in req() trennt beides von selbst: der Parameter steht
     * im PFAD, und der Pfad ist dort der Schluessel.
     */
    columns(id: string, opts: { slim: true }): Promise<ApiColumnSlim[]>;
    columns(id: string, opts?: { slim?: false }): Promise<ApiColumn[]>;
    /** dynamische Wahl: dann ist nur das gemeinsame Minimum zugesichert */
    columns(id: string, opts: { slim?: boolean }): Promise<ApiColumnSlim[]>;
    rows(
      id: string,
      params?: { limit?: number; offset?: number; sort?: string; dir?: "asc" | "desc" },
    ): Promise<{ columns: Array<{ name: string; dataType: string }>; rows: Array<Record<string, unknown>>; total: number; limit: number; offset: number; sort: string; dir: "asc" | "desc" }>;
    putField(id: string, body: Record<string, unknown>): Promise<void>;
    getForm(id: string, profile?: string): Promise<FormLayoutDto | null>;
    putForm(id: string, layout: FormLayoutDto, profile?: string): Promise<void>;
    formProfiles(id: string): Promise<string[]>;
    addColumn(id: string, body: { name: string; type: string; input?: string; decimals?: number }): Promise<{ ok: true; column: string; type: string }>;
    alterColumn(id: string, body: { column: string; newName?: string; newType?: string }): Promise<void>;
    dropColumn(id: string, body: { column: string; confirm: string }): Promise<{ ok: true; dropped: string }>;
    distinct(id: string, column: string, limit?: number): Promise<string[]>;
    /** Vorschlag naechste freie Nummer (numerisches Maximum der Spalte + 1) */
    nextNr(id: string, column: string): Promise<{ next: number | null }>;
    /** Audit-Verlauf eines Objekts (#19) — neueste zuerst */
    history(id: string, fid: string): Promise<ApiFeatureChange[]>;
    /** eine Aenderung zuruecknehmen; bei Konflikt antwortet das Backend 409.
     *  `column` grenzt die Ruecknahme auf EINE Spalte des Protokolleintrags ein —
     *  ein Eintrag kann mehrere Spalten tragen, zurueckgenommen wird die geklickte. */
    revertChange(id: string, fid: string, changeId: number, column?: string): Promise<{ ok: true }>;
    /** Flaeche mit einer Schnittlinie teilen — Attribute werden kopiert */
    splitFeature(id: string, fid: string, line: Array<[number, number]>): Promise<{ ok: true; featureIds: string[] }>;
    range(id: string, column: string): Promise<{ min: number | null; max: number | null; count: number }>;
    outline(id: string): Promise<{ geojson: { type: string; coordinates: unknown } | null }>;
    relationOptions(id: string, column: string): Promise<string[]>;
    filterCount(id: string, body: { clauses: Array<{ field: string; op: string; value: string }>; combine: "AND" | "OR" }): Promise<{ matched: number; total: number }>;
    queryList(id: string, body: { clauses: Array<{ field: string; op: string; value: string }>; combine: "AND" | "OR"; limit?: number; offset?: number; sort?: { field: string; dir: "asc" | "desc" } | null; columns?: string[] }): Promise<Array<{ fid: string; name: string | null; nr: string | null; district: string | null; bbox: [number, number, number, number]; values?: Record<string, unknown>; total?: number }>>;
    intersects(id: string, fid: string, otherId: string): Promise<Array<{ fid: string; name: string | null; overlapM2: number }>>;
    feature(id: string, fid: string): Promise<{ layerId: string; featureId: string; values: Record<string, unknown> }>;
    patchFeature(id: string, fid: string, values: Record<string, unknown>): Promise<{ ok: true; updated: number }>;
    createFeature(id: string, body: { geometry: { type: string; coordinates: unknown }; values?: Record<string, unknown> }): Promise<{ ok: true; featureId: string }>;
    /** Geodatei prüfen + serverseitig unter einem Token stagen (Vorschau der Spalten) */
    importInspect(id: string, file: File): Promise<LayerImportInspect>;
    /** gestagte Datei mit der Zuordnung in den Layer schreiben (eine Transaktion);
     *  `skipped` = Zeilen, die nicht zum Geometrietyp der Ebene passten */
    importFeatures(id: string, body: LayerImportBody): Promise<{ created: number; featureIds: string[]; skipped: number }>;
    /** feature attachments (photos/PDF/Excel in MinIO) */
    attachments(id: string, fid: string): Promise<ApiAttachment[]>;
    patchAttachment(id: string, body: { starred?: boolean }): Promise<ApiAttachment>;
    uploadAttachment(id: string, fid: string, file: File, bereich?: "maengel" | "geraet" | "pruefung"): Promise<ApiAttachment>;
    deleteAttachment(attachmentId: string): Promise<{ ok: true }>;
    /** Mängel entries (1 feature → n defects) */
    maengel(id: string, fid: string): Promise<ApiMangel[]>;
    /** Excel → Mängel import (parse + mapping preview, then confirmed run) */
    maengelImportPreview(id: string, file: File): Promise<MaengelImportPreview>;
    maengelMatchPreview(id: string, file: File, mapping: MaengelImportMapping): Promise<MaengelMatchPreview>;
    maengelImport(id: string, file: File, mapping: MaengelImportMapping, assignments?: Array<{ i: number; fid: string | null }>): Promise<{ created: number; updated: number; unchanged: number; skipped: number; unmatched: string[]; total: number; geraeteAngelegt?: number; pruefungenAngelegt?: number }>;
    createMangel(id: string, fid: string, body: Partial<ApiMangel>): Promise<ApiMangel>;
    patchMangel(mangelId: string, body: Partial<ApiMangel>): Promise<ApiMangel>;
    deleteMangel(mangelId: string): Promise<{ ok: true }>;
    /** die letzten 20 Mängel-Importe der Ebene */
    maengelImporte(layerId: string): Promise<ApiMaengelImport[]>;
    /** Statusverlauf eines Mangels: wer hat wann welchen Status gesetzt */
    maengelStatusverlauf(mangelId: string): Promise<ApiStatusAenderung[]>;
    /** löscht, was der Import angelegt hat, und setzt Geändertes zurück */
    maengelImportZuruecknehmen(layerId: string, importId: string): Promise<{ deleted: number; restored: number; alreadyUndone?: boolean }>;
    /** admin-gepflegte Auswahllisten fürs Mängel-Formular */
    maengelOptions(projectId: string): Promise<MaengelOptions>;
    putMaengelOptions(projectId: string, body: MaengelOptions): Promise<MaengelOptions>;
  };
  /** 🍔 trio only — see ImbissHit/ImbissDetail */
  imbiss: {
    area(projectId: string, bbox: string): Promise<ImbissHit[]>;
    leaderboard(projectId: string): Promise<Array<{ username: string; count: number; avg: number | null }>>;
    search(projectId: string, q: string, bbox?: string): Promise<ImbissHit[]>;
    detail(projectId: string, osmId: string): Promise<ImbissDetail>;
    ratingSummary(projectId: string, osmId: string): Promise<{ averages: Record<string, number>; stars: number | null }>;
    putRatings(projectId: string, osmId: string, values: Record<string, number | string | null>): Promise<{ ratings: ImbissDetail["ratings"]; averages: Record<string, number>; stars: number | null }>;
  };
  /** Germany-wide OSM place search (all users) — viewport-first, fuzzy */
  osm: {
    search(projectId: string, q: string, bbox?: string): Promise<OsmHit[]>;
  };
  contact: {
    send(body: { subject: string; category: string; message: string; projectId?: string | null; page?: string | null }): Promise<{ ok: true; recipients: number }>;
    /** KI formuliert aus Stichworten Betreff/Kategorie/Nachricht vor */
    draft(body: { hint: string; projectId?: string | null; page?: string | null }): Promise<{ subject: string; category: string; message: string; provider: string; nachfragen?: string[] }>;
  };
  users: {
    list(): Promise<ApiUser[]>;
    create(body: Record<string, unknown>): Promise<ApiUser>;
    update(id: string, body: Record<string, unknown>): Promise<ApiUser>;
    remove(id: string): Promise<void>;
  };
  admin: {
    testConnection(body: { host: string; port: number; database: string; username: string; password: string }): Promise<ApiConnTest>;
    geoCatalog(projectId?: string): Promise<Array<{ table: string; type: string; crs: string; rows: number }>>;
    /** Verbindungsdaten der Projekt-Geodatenbank — ohne Kennwort (Datenbank-Tab). */
    geoConnection(projectId: string): Promise<{ shared: boolean; host: string | null; port?: number; database?: string; user?: string; ssl?: boolean }>;
    uploadSources(projectId?: string): Promise<Array<{ table: string; columns: string[] }>>;
    createLayer(projectId: string, body: Record<string, unknown>): Promise<ApiLayer>;
    wmsCapabilities(url: string): Promise<{ ok: boolean; message: string; title?: string; version?: string; baseUrl?: string; tree: WmsNode | null }>;
    addWmsLayer(projectId: string, body: { url: string; layer: string; label: string; version?: string; format?: string; crs?: string[] }): Promise<{ ok: true; layerId: string }>;
    pdfThumbnail(file: File): Promise<{ dataUrl: string }>;
    renameTable(table: string, newName: string, projectId?: string): Promise<{ ok: true; table: string; updatedLayers: number }>;
    importPreview(body: { config: unknown; newId?: string }): Promise<{ projectId: string; idTaken: boolean; layerCount: number; missingTables: string[]; counts: Record<string, number> }>;
    importRun(body: { config: unknown; newId?: string }): Promise<{ ok: true; projectId: string; layerCount: number; missingTables: string[] }>;
    /** Papierkorb: geloeschte Anhaenge und Ebenen, neueste zuerst; nach 30 Tagen raeumt ein naechtlicher Lauf auf */
    trash: {
      attachments(): Promise<ApiTrashAttachment[]>;
      restoreAttachment(id: string): Promise<{ ok: true }>;
      /** endgueltig: Datei aus MinIO UND Zeile weg */
      purgeAttachment(id: string): Promise<{ ok: true }>;
      layers(): Promise<ApiTrashLayer[]>;
      restoreLayer(id: string): Promise<{ ok: true }>;
      /** endgueltig; meldet zurueck, wie viele Anhaenge der Ebene dabei mit weggeraeumt wurden */
      purgeLayer(id: string): Promise<{ purgedAttachments: number }>;
    };
  };
  valueRelations: {
    list(projectId: string): Promise<Array<Record<string, unknown>>>;
    create(projectId: string, body: Record<string, unknown>): Promise<Record<string, unknown>>;
    remove(projectId: string, id: string): Promise<void>;
    uploadCsv(projectId: string, body: { name: string; csv: string }): Promise<{ ok: true; table: string; columns: string[]; rows: number }>;
  };
  exports: {
    create(body: { layerId: string; format: string; crs: string; fields: string[]; aliases?: Record<string, string>; filter?: unknown }): Promise<{ id: string; status: string }>;
    status(id: string): Promise<{ id: string; status: "queued" | "running" | "done" | "error"; error: string | null; queuePosition: number; downloadUrl: string | null }>;
  };
  analysis: {
    create(body: { projectId: string; startLayerId: string; distances: number[]; filter?: unknown }): Promise<{ id: string; status: string }>;
    hotspots(body: { projectId: string; layerId: string; field: string; k?: number }): Promise<{ id: string; status: string }>;
    status(id: string): Promise<{
      id: string;
      status: "queued" | "running" | "done" | "error";
      step: string | null;
      detail: string | null;
      error: string | null;
      resultLayerId: string | null;
      queuePosition: number;
    }>;
    buildNetwork(body: { projectId: string; cropLayerId: string }): Promise<{ id: string; status: string }>;
    networkStatus(projectId: string): Promise<{ present: boolean; edges: number }>;
    /** Stand der naechtlichen Vorberechnung (Erreichbarkeit, Standarddistanzen) */
    accessCache(projectId: string): Promise<{
      computedAt: string | null;
      distances: number[];
      startLayerId: string | null;
      stale: boolean;
      running: boolean;
      lastError: string | null;
      defaultDistances: number[];
    }>;
    /** queued=false + alreadyQueued=true: fuer das Projekt steht schon ein Lauf an */
    recompute(projectId: string): Promise<{ ok: true; queued: boolean; alreadyQueued: boolean }>;
  };
  imports: {
    wfsCapabilities(url: string, projectId?: string | null): Promise<{ ok: boolean; message: string; title?: string; version?: string; baseUrl?: string; featureTypes: WfsFeatureType[] }>;
    createWfs(body: { projectId?: string | null; url: string; typeName: string; targetTable: string; register: boolean; srid?: number }): Promise<{ id: string; status: string }>;
    /** `cadEpsg`: Koordinatensystem einer DWG/DXF-Zeichnung (die tragen keins); fuer andere Formate ohne Wirkung. */
    inspect(file: File, projectId?: string | null, opts?: { cadEpsg?: number }): Promise<{ ok: boolean; token: string; originalName: string; layers: ImportFileLayer[] }>;
    createFile(body: { projectId?: string | null; token: string; typeName?: string; targetTable: string; register: boolean; srid?: number }): Promise<{ id: string; status: string }>;
    status(id: string): Promise<ImportJob>;
    list(projectId?: string): Promise<ImportJob[]>;
  };
  resetPassword(token: string, password: string): Promise<void>;
  /** Funktionswünsche: einreichen, abstimmen, Status (Administration) */
  wuensche: {
    liste(): Promise<ApiWunsch[]>;
    anlegen(body: { titel: string; text: string }): Promise<ApiWunsch>;
    abstimmen(id: string, wert: -1 | 0 | 1): Promise<ApiWunsch>;
    aendern(id: string, body: { status?: WunschStatus; adminNotiz?: string }): Promise<ApiWunsch>;
    loeschen(id: string): Promise<{ ok: true }>;
  };
  /** Geräteverwaltung: Spielgeräte je Fläche und als Gesamtübersicht */
  geraete: {
    vonFlaeche(layerId: string, fid: string): Promise<ApiGeraet[]>;
    anlegen(layerId: string, fid: string, body: ApiGeraetEingabe): Promise<ApiGeraet>;
    aendern(id: string, body: ApiGeraetEingabe): Promise<ApiGeraet>;
    loeschen(id: string): Promise<{ ok: true }>;
    projekt(projectId: string): Promise<{ zeilen: ApiGeraetZeile[]; statistik: ApiGeraeteStatistik }>;
    tabelle(projectId: string): Promise<{ blob: Blob; dateiname: string | null }>;
    kennwerte(projectId: string): Promise<ApiGeraeteKennwert[]>;
    kennwerteSetzen(projectId: string, kennwerte: Array<{ art: string; nutzungsdauerJahre: number | null; ersatzkosten: number | null }>): Promise<ApiGeraeteKennwert[]>;
    /** Geräteverzeichnis aus Excel/CSV: Vorschau, Zuordnung prüfen, importieren */
    importVorschau(layerId: string, file: File): Promise<GeraeteImportVorschau>;
    importZuordnung(layerId: string, file: File, mapping: GeraeteImportMapping): Promise<{ sheet: string; total: number; features: Array<{ fid: string; label: string }>; rows: GeraeteImportZeile[] }>;
    importieren(
      layerId: string,
      file: File,
      mapping: GeraeteImportMapping,
      assignments?: Array<{ i: number; fid: string | null }>,
    ): Promise<{ angelegt: number; aktualisiert: number; uebersprungen: number; unmatched: string[]; gesamt: number }>;
  };
  erinnerung: {
    lesen(projectId: string): Promise<ApiErinnerung>;
    setzen(projectId: string, body: { aktiv: boolean; rhythmus: ApiErinnerung["rhythmus"] }): Promise<ApiErinnerung>;
    probe(projectId: string): Promise<{ gesendet: boolean; leer: boolean }>;
  };
  /** Bericht für die Dienstbesprechung (Issue #131) */
  bericht: {
    lesen(projectId: string, a: BerichtAnfrage): Promise<ApiBericht>;
    excel(projectId: string, a: BerichtAnfrage): Promise<{ blob: Blob; dateiname: string | null }>;
    abos(projectId: string): Promise<ApiBerichtAbo[]>;
    aboAnlegen(projectId: string, body: Omit<ApiBerichtAbo, "id" | "letzteAt">): Promise<{ id: string }>;
    aboLoeschen(projectId: string, id: string): Promise<void>;
    aboJetzt(projectId: string, id: string): Promise<{ gesendet: boolean }>;
  };
  /** Projektverwaltung (Issues #125/#129) */
  verwaltung: {
    einladen(projectId: string, body: { email: string; displayName: string; role: ApiProjectMember["role"]; nachricht?: string }): Promise<{ angelegt: boolean; hinzugefuegt: boolean; name: string; benutzername?: string; mail: boolean; startkennwort?: string }>;
  };
  /** Lizenzübersicht (Issue #126) */
  lizenz: {
    uebersicht(): Promise<ApiLizenz[]>;
    projekt(projectId: string): Promise<ApiLizenz>;
    setzen(projectId: string, body: { paket: string; plaetze: number | null; bis: string | null; notiz: string; buchungen: Array<{ addon: string; seit: string | null; bis: string | null; notiz: string }> }): Promise<ApiLizenz>;
  };
  /** Zuständigkeiten, Benachrichtigungen, Vertretung (Issue #134) */
  benachrichtigung: {
    lesen(projectId: string): Promise<ApiBenachrichtigungen>;
    einstellen(projectId: string, body: { aktiv: boolean; ereignisse: string[] }): Promise<{ aktiv: boolean; ereignisse: string[] }>;
    zustaendigAnlegen(projectId: string, body: ApiZustaendigkeitEingabe): Promise<{ id: string | null; schonDa: boolean }>;
    zustaendigLoeschen(projectId: string, id: string): Promise<void>;
    vertretung(): Promise<ApiVertretung>;
    vertretungSetzen(vertretungId: string | null): Promise<ApiVertretung>;
  };
  /** Routen (Issues #122–#124) — ohne Baustein */
  routen: {
    liste(projectId: string): Promise<ApiRoutenListe>;
    anlegen(projectId: string, body: ApiRoutenEingabe): Promise<ApiRoute>;
    aendern(id: string, body: Partial<ApiRoutenEingabe>): Promise<ApiRoute>;
    loeschen(id: string): Promise<void>;
    uebernehmen(id: string, zurueck: boolean): Promise<ApiRoute>;
    optimieren(projectId: string, body: { halte: Array<{ layerId: string; featureId: string }>; start?: ApiRoutenPunkt | null }): Promise<ApiRoutenReihenfolge>;
    planen(projectId: string, body: { ortsteile: string[]; auswahl: "alle" | "faellig"; maxJeTag: number; start?: ApiRoutenPunkt | null }): Promise<ApiRoutenPlan>;
    importVorschau(projectId: string, file: File): Promise<ApiRoutenImport>;
    datei(id: string, format: RoutenFormat): Promise<{ blob: Blob; dateiname: string | null }>;
    googleLinks(id: string): Promise<{ links: string[] }>;
  };
  /** Spielplatzprüfung (Baustein pruefung) */
  pruefung: {
    uebersicht(projectId: string): Promise<ApiPruefUebersicht>;
    intervalleSetzen(projectId: string, intervalle: ApiPruefIntervall[]): Promise<ApiPruefIntervall[]>;
    vorlagen(projectId: string): Promise<ApiPruefVorlage[]>;
    /** Kontrollgang: mehrere Spielplätze auf einmal als „ohne Befund" eintragen */
    kontrollgang(
      projectId: string,
      body: { datum?: string; pruefer?: string; firma?: string; bemerkung?: string; flaechen: Array<{ layerId: string; featureId: string }> },
    ): Promise<{ eingetragen: number; uebersprungen: number; datum: string }>;
    kalender(projectId: string): Promise<{ blob: Blob; dateiname: string | null }>;
    /** Daten für das Sammel-PDF; höchstens `max` Spielplätze je Aufruf */
    akten(projectId: string, opts?: { route?: string; status?: string; max?: number }): Promise<{ gesamt: number; gewaehlt: number; akten: ApiAkte[] }>;
    vorlageSetzen(projectId: string, art: Pruefart, punkte: Array<{ key: string; text: string; kategorie: string }> | null): Promise<ApiPruefVorlage[]>;
    flaeche(layerId: string, fid: string): Promise<ApiPruefFlaechenDetail>;
    planSetzen(layerId: string, fid: string, plan: Array<{ art: PruefPlanart; intervallTage: number | null; aktiv: boolean }>): Promise<ApiPruefFlaechenDetail>;
    beginnen(layerId: string, fid: string, body: { art: Pruefart; datum?: string; pruefer?: string; firma?: string }): Promise<ApiPruefung>;
    nachtragen(layerId: string, fid: string, body: { art: Pruefart; datum: string; pruefer: string; firma: string; ergebnis: PruefErgebnis; bemerkung: string }): Promise<ApiPruefung>;
    aendern(id: string, body: Partial<Pick<ApiPruefung, "datum" | "pruefer" | "firma" | "bemerkung" | "punkte">> & { ergebnis?: PruefErgebnis | null }): Promise<ApiPruefung>;
    loeschen(id: string): Promise<{ ok: true }>;
    abschliessen(id: string, ergebnis?: PruefErgebnis): Promise<ApiPruefung>;
  };
  auditLog(): Promise<Array<{ id: number; username: string; layerId: string; layerLabel: string | null; featureId: string; column: string; oldValue: string | null; newValue: string | null; at: string }>>;
  projectConfig(id: string): Promise<Record<string, unknown>>;
  /** oeffentliche Meldeseite — ohne Anmeldung */
  melden: {
    seite(kennung: string): Promise<ApiMeldeSeite>;
    senden(kennung: string, body: ApiMeldungSenden): Promise<{ ok: true; vorgang: string | null }>;
    /** Umriss EINER Flaeche fuer die Uebersichtskarte — erst nach der Auswahl. */
    umriss(kennung: string, layerId: string, featureId: string): Promise<{ geojson: unknown; bbox: [number, number, number, number] }>;
    /** Geräte einer Fläche für „Welches Gerät?" (leer ohne Geräteverwaltung) */
    geraete(kennung: string, layerId: string, featureId: string): Promise<Array<{ id: string; art: string; name: string }>>;
  };
  meldungen: {
    liste(projectId: string): Promise<ApiMeldungenListe>;
    flaechen(projectId: string): Promise<ApiSpielflaeche[]>;
    /** Formulardaten fuer das Meldeportal der Anwendung */
    seite(projectId: string): Promise<ApiMeldeSeite>;
    /** Meldung im Meldeportal der Anwendung erfassen */
    erfassen(projectId: string, body: ApiMeldungInhalt): Promise<{ id: string; vorgang: string }>;
    aendern(
      id: string,
      body: {
        status?: Exclude<MeldungStatus, "mangel">;
        kategorie?: string;
        unterart?: string;
        begruendung?: string;
        flaeche?: { layerId: string; featureId: string } | null;
      },
    ): Promise<{ ok: true }>;
    zuMangel(id: string, body?: { severity?: "gering" | "mittel" | "hoch" | "gefahr" }): Promise<{ mangelId: string; layerId: string; featureId: string }>;
    portal(projectId: string): Promise<{ portal: ApiMeldeportal | null; vorbelegung: ApiMeldungsTyp[] }>;
    portalSpeichern(projectId: string, body: { aktiv: boolean; akutMail: string; typologie: ApiMeldungsTyp[] }): Promise<ApiMeldeportal>;
    neueKennung(projectId: string): Promise<ApiMeldeportal>;
    /** Bildadresse fuer <img> — laeuft ueber das Sitzungscookie */
    fotoUrl(id: string, n: number): string;
    /** KI ordnet eine Meldung ohne Fläche zu (Baustein KI) */
    kiZuordnen(id: string): Promise<{ ergebnis: "zugeordnet" | "vorgeschlagen" | "nichts" | "aus" | "schon_zugeordnet" }>;
  };
  /** Oeffentlicher Demo-Einstieg — meldet als Lese-Demo-Konto an. */
  demoLogin(): Promise<SessionUser>;
} = {
  async login({ identifier, password }: LoginRequest): Promise<SessionUser | null> {
    try {
      const r = await post<{ user: SessionUser }>("/auth/login", { identifier, password });
      // Personenwechsel: nichts aus der vorigen Sitzung darf durchgereicht werden.
      etagSpeicherLeeren();
      demoModusSetzen(false);
      return r.user;
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return null;
      throw e;
    }
  },
  async demoLogin(): Promise<SessionUser> {
    const r = await post<{ user: SessionUser }>("/auth/demo");
    etagSpeicherLeeren();
    demoModusSetzen(true);
    return r.user;
  },
  async logout() {
    await post("/auth/logout");
    etagSpeicherLeeren();
    demoModusSetzen(false);
  },
  async forgotPassword(email: string) {
    await post("/auth/forgot", { email });
  },
  async resetPassword(token: string, password: string) {
    await post("/auth/reset", { token, password });
  },
  async me() {
    const r = await get<{ user: SessionUser | null }>("/auth/me");
    // Pflicht, nicht Beiwerk: das Sitzungs-Cookie lebt ein Neuladen lang
    // weiter, und die App bootstrappt ueber genau diesen Aufruf. Ohne ihn waere
    // die Simulation nach jedem F5 tot und jeder Schreibversuch liefe in den
    // 403 des Backend-Riegels — bei sichtbar gebliebenen Knoepfen.
    demoModusSetzen(!!r.user?.demo);
    return r.user;
  },
  async account(update) {
    const r = await post<{ user: SessionUser }>("/auth/account", update);
    return r.user;
  },
  async changePassword(currentPassword, newPassword) {
    await post("/auth/password", { currentPassword, newPassword });
  },
  uebersicht: {
    grenzen: () => get("/uebersicht/grenzen"),
    spielplaetze: () => get("/uebersicht/spielplaetze"),
  },
  projects: {
    list: () => get("/projects"),
    get: (id) => get(`/projects/${id}`),
    update: (id, body) => patch(`/projects/${id}`, body),
    remove: (id) => del(`/projects/${id}`),
    setGeoConnection: (id, body) => post(`/projects/${id}/geo-connection`, body),
    clearGeoConnection: (id) => del(`/projects/${id}/geo-connection`),
    stats: (id) => get(`/projects/${id}/stats`),
    members: {
      list: (id) => get(`/projects/${id}/members`),
      set: (id, userId, role) => put(`/projects/${id}/members/${userId}`, { role }),
      remove: (id, userId) => del(`/projects/${id}/members/${userId}`),
    },
    setAddons: (id, addons) => patch(`/projects/${id}/addons`, { addons }),
    setCategoryColors: (id, farben) => put(`/projects/${id}/category-colors`, farben),
    aggregate: (id, query) => post(`/projects/${id}/aggregate`, query),
    maengelStats: (id, body) => post(`/projects/${id}/maengel/stats`, body),
    maengelTabelle: (id, body) => postDatei(`/projects/${id}/maengel/tabelle`, body),
    maengelListe: (id, body) => post(`/projects/${id}/maengel/liste`, body),
    maengelStatusSetzen: (id, body) => post(`/projects/${id}/maengel/status`, body),
    hinweise: (id) => get(`/projects/${id}/hinweise`),
    layers: (id) => get(`/projects/${id}/layers`),
    search: (id, q) => get(`/projects/${id}/search?q=${encodeURIComponent(q)}`),
    photoSearch: (id, q, limit) => get(`/projects/${id}/photos/search?q=${encodeURIComponent(q)}${limit ? `&limit=${limit}` : ""}`),
    recent: (id) => get(`/projects/${id}/recent`),
    charts: {
      list: (projectId) => get(`/projects/${projectId}/charts`),
      create: (projectId, body) => post(`/projects/${projectId}/charts`, body),
      update: (projectId, id, body) => patch(`/projects/${projectId}/charts/${id}`, body),
      remove: (projectId, id) => del(`/projects/${projectId}/charts/${id}`),
    },
    filters: {
      list: (projectId) => get(`/projects/${projectId}/filters`),
      create: (projectId, body) => post(`/projects/${projectId}/filters`, body),
      update: (projectId, id, body) => patch(`/projects/${projectId}/filters/${id}`, body),
      used: (projectId, id) => post(`/projects/${projectId}/filters/${id}/used`, {}),
      remove: (projectId, id) => del(`/projects/${projectId}/filters/${id}`),
    },
    mapViews: {
      list: (projectId) => get(`/projects/${projectId}/map-views`),
      create: (projectId, body) => post(`/projects/${projectId}/map-views`, body),
      rename: (projectId, id, name) => patch(`/projects/${projectId}/map-views/${id}`, { name }),
      setGfi: (projectId, id, layerId, profile) => patch(`/projects/${projectId}/map-views/${id}/gfi`, { layerId, profile }),
      remove: (projectId, id) => del(`/projects/${projectId}/map-views/${id}`),
    },
    population: {
      stichtage: (projectId) => get(`/projects/${projectId}/population/stichtage`),
      rows: (projectId, stichtag) => get(`/projects/${projectId}/population${stichtag ? `?stichtag=${encodeURIComponent(stichtag)}` : ""}`),
      inspect: (projectId, file) => {
        const form = new FormData();
        form.append("file", file);
        return postForm(`/projects/${projectId}/population/inspect`, form);
      },
      apply: (projectId, body) => post(`/projects/${projectId}/population/apply`, body),
      remove: (projectId, stichtag) => del(`/projects/${projectId}/population/${encodeURIComponent(stichtag)}`),
    },
  },
  layers: {
    update: (id, body) => patch(`/layers/${id}`, body),
    remove: (id) => del(`/layers/${id}`),
    columns: ladeSpalten,
    rows: (id, params) => {
      const q = new URLSearchParams();
      if (params?.limit) q.set("limit", String(params.limit));
      if (params?.offset) q.set("offset", String(params.offset));
      if (params?.sort) q.set("sort", params.sort);
      if (params?.dir) q.set("dir", params.dir);
      const qs = q.toString();
      return get(`/layers/${id}/rows${qs ? `?${qs}` : ""}`);
    },
    putField: (id, body) => put(`/layers/${id}/fields`, body),
    getForm: (id, profile) => get(`/layers/${id}/form${profile ? `?profile=${encodeURIComponent(profile)}` : ""}`),
    putForm: (id, layout, profile) => put(`/layers/${id}/form${profile ? `?profile=${encodeURIComponent(profile)}` : ""}`, layout),
    formProfiles: (id) => get(`/layers/${id}/form-profiles`),
    addColumn: (id, body) => post(`/layers/${id}/sql/add-column`, body),
    alterColumn: (id, body) => post(`/layers/${id}/sql/alter-column`, body),
    dropColumn: (id, body) => post(`/layers/${id}/sql/drop-column`, body),
    distinct: (id, column, limit) => get(`/layers/${id}/distinct/${encodeURIComponent(column)}${limit ? `?limit=${limit}` : ""}`),
    nextNr: (id, column) => get(`/layers/${id}/next-nr/${encodeURIComponent(column)}`),
    history: (id, fid) => get(`/layers/${id}/features/${encodeURIComponent(fid)}/history`),
    revertChange: (id, fid, changeId, column) => post(`/layers/${id}/features/${encodeURIComponent(fid)}/revert`, column ? { changeId, column } : { changeId }),
    splitFeature: (id, fid, line) => post(`/layers/${id}/features/${encodeURIComponent(fid)}/split`, { line }),
    range: (id, column) => get(`/layers/${id}/range/${encodeURIComponent(column)}`),
    outline: (id) => get(`/layers/${id}/outline`),
    relationOptions: (id, column) => get(`/layers/${id}/fields/${encodeURIComponent(column)}/relation-options`),
    filterCount: (id, body) => post(`/layers/${id}/query/count`, body),
    queryList: (id, body) => post(`/layers/${id}/query/list`, body),
    intersects: (id, fid, otherId) => get(`/layers/${id}/features/${encodeURIComponent(fid)}/intersects/${otherId}`),
    feature: (id, fid) => get(`/layers/${id}/features/${encodeURIComponent(fid)}`),
    patchFeature: (id, fid, values) => patch(`/layers/${id}/features/${encodeURIComponent(fid)}`, { values }),
    createFeature: (id, body) => post(`/layers/${id}/features`, body),
    importInspect: (id, file) => {
      const form = new FormData();
      form.append("file", file);
      return postForm(`/layers/${id}/features/import/inspect`, form);
    },
    importFeatures: (id, body) => post(`/layers/${id}/features/import`, body),
    attachments: (id, fid) => get(`/layers/${id}/features/${encodeURIComponent(fid)}/attachments`),
    patchAttachment: (id, body) => patch(`/attachments/${id}`, body),
    uploadAttachment: (id, fid, file, bereich) => {
      const form = new FormData();
      form.append("file", file);
      // markiert den Anhang als zum Maengel-Reiter gehoerig — der Steckbrief
      // zeigt ihn dann nicht mehr mit an
      if (bereich) form.append("bereich", bereich);
      return postForm(`/layers/${id}/features/${encodeURIComponent(fid)}/attachments`, form);
    },
    deleteAttachment: (attachmentId) => del(`/attachments/${attachmentId}`),
    maengel: (id, fid) => get(`/layers/${id}/features/${encodeURIComponent(fid)}/maengel`),
    maengelImportPreview: (id, file) => {
      const form = new FormData();
      form.append("file", file);
      return postForm(`/layers/${id}/maengel/import/preview`, form);
    },
    maengelMatchPreview: (id, file, mapping) => {
      const form = new FormData();
      form.append("file", file);
      form.append("mapping", JSON.stringify(mapping));
      return postForm(`/layers/${id}/maengel/import/match-preview`, form);
    },
    maengelImport: (id, file, mapping, assignments) => {
      const form = new FormData();
      form.append("file", file);
      form.append("mapping", JSON.stringify(mapping));
      if (assignments) form.append("assignments", JSON.stringify(assignments));
      return postForm(`/layers/${id}/maengel/import`, form);
    },
    createMangel: (id, fid, body) => post(`/layers/${id}/features/${encodeURIComponent(fid)}/maengel`, body),
    patchMangel: (mangelId, body) => patch(`/maengel/${mangelId}`, body),
    deleteMangel: (mangelId) => del(`/maengel/${mangelId}`),
    maengelImporte: (layerId) => get(`/layers/${encodeURIComponent(layerId)}/maengel/imports`),
    maengelStatusverlauf: (mangelId) => get(`/maengel/${encodeURIComponent(mangelId)}/statusverlauf`),
    maengelImportZuruecknehmen: (layerId, importId) => post(`/layers/${encodeURIComponent(layerId)}/maengel/import/${encodeURIComponent(importId)}/undo`),
    maengelOptions: (projectId) => get(`/projects/${projectId}/maengel-options`),
    putMaengelOptions: (projectId, body) => put(`/projects/${projectId}/maengel-options`, body),
  },
  imbiss: {
    area: (projectId: string, bbox: string): Promise<ImbissHit[]> => get(`/projects/${projectId}/imbiss/area?bbox=${bbox}`),
    leaderboard: (projectId: string) => get(`/projects/${projectId}/imbiss/leaderboard`),
    search: (projectId: string, q: string, bbox?: string): Promise<ImbissHit[]> => get(`/projects/${projectId}/imbiss/search?q=${encodeURIComponent(q)}${bbox ? `&bbox=${bbox}` : ''}`),
    detail: (projectId: string, osmId: string): Promise<ImbissDetail> => get(`/projects/${projectId}/imbiss/poi/${osmId}`),
    /** Nur der Bewertungsspiegel. Anders als `detail` loest das KEINEN
     *  Overpass-Abruf und keine Bildersuche aus — fuer den Karten-Tooltip,
     *  der ausser den Sternen nichts davon braucht. */
    ratingSummary: (projectId: string, osmId: string): Promise<{ averages: Record<string, number>; stars: number | null }> =>
      get(`/projects/${projectId}/imbiss/poi/${osmId}/ratings`),
    putRatings: (projectId: string, osmId: string, values: Record<string, number | string | null>) => put(`/projects/${projectId}/imbiss/poi/${osmId}/ratings`, { values }),
  },
  osm: {
    search: (projectId: string, q: string, bbox?: string): Promise<OsmHit[]> => get(`/projects/${projectId}/osm-search?q=${encodeURIComponent(q)}${bbox ? `&bbox=${bbox}` : ''}`),
  },
  contact: {
    send: (body) => post("/contact", body),
    draft: (body) => post("/contact/draft", body),
  },
  users: {
    list: () => get("/users"),
    create: (body) => post("/users", body),
    update: (id, body) => patch(`/users/${id}`, body),
    remove: (id) => del(`/users/${id}`),
  },
  admin: {
    testConnection: (body) => post("/admin/connection/test", body),
    geoCatalog: (projectId) => get(`/admin/geo-catalog${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ""}`),
    geoConnection: (projectId) => get(`/projects/${projectId}/geo-connection`),
    uploadSources: (projectId) => get(`/admin/upload-sources${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ""}`),
    createLayer: (projectId, body) => post(`/projects/${projectId}/layers`, body),
    wmsCapabilities: (url) => post("/admin/wms/capabilities", { url }),
    addWmsLayer: (projectId, body) => post(`/projects/${projectId}/wms-layer`, body),
    pdfThumbnail: (file) => {
      const fd = new FormData();
      fd.append("file", file);
      return postForm("/admin/pdf-thumbnail", fd);
    },
    renameTable: (table, newName, projectId) => post("/admin/rename-table", { table, newName, projectId }),
    importPreview: (body) => post("/admin/config-import/preview", body),
    importRun: (body) => post("/admin/config-import", body),
    trash: {
      attachments: () => get("/admin/trash/attachments"),
      restoreAttachment: (id) => post(`/admin/trash/attachments/${encodeURIComponent(id)}/restore`),
      purgeAttachment: (id) => del(`/admin/trash/attachments/${encodeURIComponent(id)}`),
      layers: () => get("/admin/trash/layers"),
      restoreLayer: (id) => post(`/admin/trash/layers/${encodeURIComponent(id)}/restore`),
      purgeLayer: (id) => del(`/admin/trash/layers/${encodeURIComponent(id)}`),
    },
  },
  valueRelations: {
    list: (projectId) => get(`/projects/${projectId}/value-relations`),
    create: (projectId, body) => post(`/projects/${projectId}/value-relations`, body),
    remove: (projectId, id) => del(`/projects/${projectId}/value-relations/${id}`),
    uploadCsv: (projectId, body) => post(`/projects/${projectId}/value-relations/csv`, body),
  },
  exports: {
    create: (body) => post("/exports", body),
    status: (id) => get(`/exports/${id}`),
  },
  analysis: {
    create: (body) => post("/analysis/isochrones", body),
    hotspots: (body) => post("/analysis/hotspots", body),
    status: (id) => get(`/analysis/${id}`),
    buildNetwork: (body) => post("/analysis/network/build", body),
    networkStatus: (projectId) => get(`/analysis/network/${projectId}`),
    accessCache: (projectId) => get(`/analysis/${projectId}/access-cache`),
    recompute: (projectId) => post(`/analysis/${projectId}/recompute`),
  },
  imports: {
    wfsCapabilities: (url, projectId) => post("/imports/wfs/capabilities", { url, projectId }),
    createWfs: (body) => post("/imports/wfs", body),
    inspect: (file, projectId, opts) => {
      const fd = new FormData();
      fd.append("file", file);
      // projectId als Query: multipart wird erst nach den Guards geparst.
      // cadEpsg ebenso — der Server wandelt die Zeichnung noch im selben Request um.
      const q = new URLSearchParams();
      if (projectId) q.set("projectId", projectId);
      if (opts?.cadEpsg) q.set("cadEpsg", String(opts.cadEpsg));
      const qs = q.toString();
      return postForm(`/imports/inspect${qs ? `?${qs}` : ""}`, fd);
    },
    createFile: (body) => post("/imports/file", body),
    status: (id) => get(`/imports/${id}`),
    list: (projectId) => get(`/imports${projectId ? `?projectId=${encodeURIComponent(projectId)}` : ""}`),
  },
  wuensche: {
    liste: () => get("/wuensche"),
    anlegen: (body) => post("/wuensche", body),
    abstimmen: (id, wert) => put(`/wuensche/${encodeURIComponent(id)}/stimme`, { wert }),
    aendern: (id, body) => patch(`/wuensche/${encodeURIComponent(id)}`, body),
    loeschen: (id) => del(`/wuensche/${encodeURIComponent(id)}`),
  },
  geraete: {
    vonFlaeche: (layerId, fid) => get(`/layers/${encodeURIComponent(layerId)}/features/${encodeURIComponent(fid)}/geraete`),
    anlegen: (layerId, fid, body) => post(`/layers/${encodeURIComponent(layerId)}/features/${encodeURIComponent(fid)}/geraete`, body),
    aendern: (id, body) => patch(`/geraete/${encodeURIComponent(id)}`, body),
    loeschen: (id) => del(`/geraete/${encodeURIComponent(id)}`),
    projekt: (projectId) => get(`/projects/${encodeURIComponent(projectId)}/geraete`),
    tabelle: (projectId) => postDatei(`/projects/${encodeURIComponent(projectId)}/geraete/tabelle`, {}),
    kennwerte: (projectId) => get(`/projects/${encodeURIComponent(projectId)}/geraete/kennwerte`),
    kennwerteSetzen: (projectId, kennwerte) => put(`/projects/${encodeURIComponent(projectId)}/geraete/kennwerte`, { kennwerte }),
    importVorschau: (layerId, file) => {
      const form = new FormData();
      form.append("file", file);
      return postForm(`/layers/${encodeURIComponent(layerId)}/geraete/import/vorschau`, form);
    },
    importZuordnung: (layerId, file, mapping) => {
      const form = new FormData();
      form.append("file", file);
      form.append("mapping", JSON.stringify(mapping));
      return postForm(`/layers/${encodeURIComponent(layerId)}/geraete/import/zuordnung`, form);
    },
    importieren: (layerId, file, mapping, assignments) => {
      const form = new FormData();
      form.append("file", file);
      form.append("mapping", JSON.stringify(mapping));
      if (assignments) form.append("assignments", JSON.stringify(assignments));
      return postForm(`/layers/${encodeURIComponent(layerId)}/geraete/import`, form);
    },
  },
  erinnerung: {
    lesen: (projectId) => get(`/projects/${encodeURIComponent(projectId)}/erinnerung`),
    setzen: (projectId, body) => put(`/projects/${encodeURIComponent(projectId)}/erinnerung`, body),
    probe: (projectId) => post(`/projects/${encodeURIComponent(projectId)}/erinnerung/probe`, {}),
  },
  verwaltung: {
    einladen: (projectId, body) => post(`/projects/${encodeURIComponent(projectId)}/einladen`, body),
  },
  bericht: {
    lesen: (projectId, a) => get(`/projects/${encodeURIComponent(projectId)}/bericht?${berichtQuery(a)}`),
    excel: (projectId, a) => getDatei(`/projects/${encodeURIComponent(projectId)}/bericht/excel?${berichtQuery(a)}`),
    abos: (projectId) => get(`/projects/${encodeURIComponent(projectId)}/bericht/abos`),
    aboAnlegen: (projectId, body) => post(`/projects/${encodeURIComponent(projectId)}/bericht/abos`, body),
    aboLoeschen: (projectId, id) => del(`/projects/${encodeURIComponent(projectId)}/bericht/abos/${encodeURIComponent(id)}`),
    aboJetzt: (projectId, id) => post(`/projects/${encodeURIComponent(projectId)}/bericht/abos/${encodeURIComponent(id)}/jetzt`, {}),
  },
  lizenz: {
    uebersicht: () => get("/admin/lizenzen"),
    projekt: (projectId) => get(`/projects/${encodeURIComponent(projectId)}/lizenz`),
    setzen: (projectId, body) => put(`/projects/${encodeURIComponent(projectId)}/lizenz`, body),
  },
  benachrichtigung: {
    lesen: (projectId) => get(`/projects/${encodeURIComponent(projectId)}/benachrichtigungen`),
    einstellen: (projectId, body) => put(`/projects/${encodeURIComponent(projectId)}/benachrichtigungen`, body),
    zustaendigAnlegen: (projectId, body) => post(`/projects/${encodeURIComponent(projectId)}/zustaendigkeiten`, body),
    zustaendigLoeschen: (projectId, id) => del(`/projects/${encodeURIComponent(projectId)}/zustaendigkeiten/${encodeURIComponent(id)}`),
    vertretung: () => get("/me/vertretung"),
    vertretungSetzen: (vertretungId) => put("/me/vertretung", { vertretungId }),
  },
  routen: {
    liste: (projectId) => get(`/projects/${encodeURIComponent(projectId)}/routen`),
    anlegen: (projectId, body) => post(`/projects/${encodeURIComponent(projectId)}/routen`, body),
    aendern: (id, body) => patch(`/routen/${encodeURIComponent(id)}`, body),
    loeschen: (id) => del(`/routen/${encodeURIComponent(id)}`),
    uebernehmen: (id, zurueck) => post(`/routen/${encodeURIComponent(id)}/uebernehmen`, { zurueck }),
    optimieren: (projectId, body) => post(`/projects/${encodeURIComponent(projectId)}/routen/optimieren`, body),
    planen: (projectId, body) => post(`/projects/${encodeURIComponent(projectId)}/routen/planen`, body),
    importVorschau: (projectId, file) => {
      const form = new FormData();
      form.append("file", file);
      return postForm(`/projects/${encodeURIComponent(projectId)}/routen/import`, form);
    },
    datei: (id, format) => getDatei(`/routen/${encodeURIComponent(id)}/export?format=${format}`),
    googleLinks: (id) => get(`/routen/${encodeURIComponent(id)}/export?format=google`),
  },
  pruefung: {
    uebersicht: (projectId) => get(`/projects/${encodeURIComponent(projectId)}/pruefungen`),
    intervalleSetzen: (projectId, intervalle) => put(`/projects/${encodeURIComponent(projectId)}/pruefungen/intervalle`, { intervalle }),
    vorlagen: (projectId) => get(`/projects/${encodeURIComponent(projectId)}/pruefungen/vorlagen`),
    kontrollgang: (projectId, body) => post(`/projects/${encodeURIComponent(projectId)}/pruefungen/kontrollgang`, body),
    kalender: (projectId) => getDatei(`/projects/${encodeURIComponent(projectId)}/pruefungen/kalender.ics`),
    akten: (projectId, opts) => {
      const p = new URLSearchParams();
      if (opts?.route) p.set("route", opts.route);
      if (opts?.status) p.set("status", opts.status);
      if (opts?.max) p.set("max", String(opts.max));
      const q = p.toString();
      return get(`/projects/${encodeURIComponent(projectId)}/pruefungen/akten${q ? `?${q}` : ""}`);
    },
    vorlageSetzen: (projectId, art, punkte) => put(`/projects/${encodeURIComponent(projectId)}/pruefungen/vorlagen/${art}`, { punkte }),
    flaeche: (layerId, fid) => get(`/layers/${encodeURIComponent(layerId)}/features/${encodeURIComponent(fid)}/pruefungen`),
    planSetzen: (layerId, fid, plan) => put(`/layers/${encodeURIComponent(layerId)}/features/${encodeURIComponent(fid)}/pruefplan`, { plan }),
    beginnen: (layerId, fid, body) => post(`/layers/${encodeURIComponent(layerId)}/features/${encodeURIComponent(fid)}/pruefungen`, body),
    nachtragen: (layerId, fid, body) => post(`/layers/${encodeURIComponent(layerId)}/features/${encodeURIComponent(fid)}/pruefungen/nachtrag`, body),
    aendern: (id, body) => patch(`/pruefungen/${encodeURIComponent(id)}`, body),
    loeschen: (id) => del(`/pruefungen/${encodeURIComponent(id)}`),
    abschliessen: (id, ergebnis) => post(`/pruefungen/${encodeURIComponent(id)}/abschliessen`, ergebnis ? { ergebnis } : {}),
  },
  auditLog: () => get("/admin/audit"),
  projectConfig: (id) => get(`/projects/${id}/config`),
  melden: {
    seite: (kennung) => get(`/melden/${encodeURIComponent(kennung)}`),
    senden: (kennung, body) => post(`/melden/${encodeURIComponent(kennung)}`, body),
    umriss: (kennung, layerId, featureId) =>
      get(`/melden/${encodeURIComponent(kennung)}/flaeche/${encodeURIComponent(layerId)}/${encodeURIComponent(featureId)}`),
    geraete: (kennung, layerId, featureId) =>
      get(`/melden/${encodeURIComponent(kennung)}/flaeche/${encodeURIComponent(layerId)}/${encodeURIComponent(featureId)}/geraete`),
  },
  meldungen: {
    liste: (projectId) => get(`/projects/${encodeURIComponent(projectId)}/meldungen`),
    flaechen: (projectId) => get(`/projects/${encodeURIComponent(projectId)}/meldungen/flaechen`),
    seite: (projectId) => get(`/projects/${encodeURIComponent(projectId)}/meldeseite`),
    erfassen: (projectId, body) => post(`/projects/${encodeURIComponent(projectId)}/meldungen`, body),
    aendern: (id, body) => patch(`/meldungen/${encodeURIComponent(id)}`, body),
    zuMangel: (id, body) => post(`/meldungen/${encodeURIComponent(id)}/mangel`, body ?? {}),
    portal: (projectId) => get(`/projects/${encodeURIComponent(projectId)}/meldeportal`),
    portalSpeichern: (projectId, body) => put(`/projects/${encodeURIComponent(projectId)}/meldeportal`, body),
    neueKennung: (projectId) => post(`/projects/${encodeURIComponent(projectId)}/meldeportal/kennung`),
    fotoUrl: (id, n) => `/api/meldungen/${encodeURIComponent(id)}/fotos/${n}`,
    kiZuordnen: (id) => post(`/meldungen/${encodeURIComponent(id)}/ki`),
  },
};

export const api = http;
